/**
 * ============================================================
 *  DEBRIS FLOW ANALYSIS SUITE — React Frontend v12.0
 *  GeoHazard Analysis Platform
 * ============================================================
 *  v12.0 — Canadian IDF, Trigger Assessment, ECCC SWOB-ML, IDF log-log curves, PDF deployer changelog
 *   - Left sidebar navigation (25% panel)
 *   - Each module has "Analyze" + "Learn" sub-tabs
 *   - Rich in-depth Learn panels per module
 *   - Tooltips on all inputs/outputs
 *   - Step-by-step CalcPanels
 *   - Full reference lists
 * ============================================================
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, LogarithmicScale,
  BarElement, LineElement, PointElement, Title, Tooltip, Legend,
  Filler, ScatterController
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import {
  Activity, Zap, Mountain, CloudRain, Radio, Download, RefreshCw,
  Upload, AlertTriangle, CheckCircle, Info, TrendingUp, Layers,
  ChevronRight, BarChart2, Cpu, BookOpen, ChevronDown, ChevronUp,
  HelpCircle, FlaskConical, GraduationCap, Waves, MapPin, Search,
  Globe, Thermometer, Wind, Droplets, TriangleAlert
} from 'lucide-react';

ChartJS.register(CategoryScale, LinearScale, LogarithmicScale,
  BarElement, LineElement, PointElement, Title, Tooltip, Legend,
  Filler, ScatterController);

const API = `${window.location.origin}/api`;

// ═══════════════════════════════════════════════════════════
// TOOLTIP DEFINITIONS
// ═══════════════════════════════════════════════════════════
const TIPS = {
  slopeAngle:      { title:'Slope Angle θ', eq:'C∞=ρ·tanθ/[(σ-ρ)(tanφ-tanθ)]', def:'Channel gradient. θ₁≈14.5° min initiation; θ₂≈18–22° max stable bed (Eqs. 3.4, 3.8). Above angle of repose → total bed failure.', range:'Deposition: <θ₁  |  Transport: θ₁–θ₂  |  Initiation: >θ₂  |  Max: φ (repose angle)', ref:'Takahashi (2014) §3.1, Eqs. 3.4, 3.8' },
  grainDiameter:   { title:'Grain Diameter dₚ', eq:'U=(2/5dₚ)·√(g·sinθ/0.02)·[…]·h^(3/2)', def:'D₅₀ median grain size in mm. Controls velocity, Bagnold number, settling velocity, and relative depth h/dₚ.', range:'Gravel: 2–64 mm  |  Cobble: 64–256 mm  |  Boulder: >256 mm', ref:'Takahashi (2014) Eq. 2.29, Table 2.1' },
  waterDepth:      { title:'Flow Depth h', eq:'U∝h^(3/2) (stony DF)', def:'Total mixture depth in channel. Controls velocity via h^(3/2) power law (dilatant model) or Manning for turbulent flow.', range:'Field range: 0.3–10 m  |  h/dₚ<10: stony  |  h/dₚ>30: turbulent-muddy', ref:'Takahashi (2014) Eq. 2.29; §2.6' },
  particleDensity: { title:'Particle Density σ', eq:'ρ_T=σ·C∞+(1-C∞)·ρ', def:'Bulk density of solid grains. Higher σ/ρ ratio reduces C∞ and increases dispersive pressure.', range:'Granite: 2600–2700  |  Sandstone: 2200–2500  |  Basalt: 2700–2900 kg/m³', ref:'Takahashi (2014) Eq. 2.37' },
  fluidDensity:    { title:'Fluid Density ρ', eq:'C∞=ρ·tanθ/[(σ-ρ)(tanφ-tanθ)]', def:'Density of interstitial fluid. Higher ρ (muddy slurry) increases C∞ — heavy interstitial fluid boosts sediment carrying capacity.', range:'Clear water: 1000  |  Silt slurry: 1100–1300  |  Dense mud: >1300 kg/m³', ref:'Takahashi (2014) §2.2.2, Fig. 2.8' },
  concentration:   { title:'Equilibrium Concentration C∞', eq:'C∞=ρ·tanθ/[(σ-ρ)(tanφ-tanθ)]', def:'Volumetric solids fraction at dynamic equilibrium. Flow erodes if C<C∞; deposits if C>C∞. Max experimentally ≈0.9C*.', range:'Stony DF: C∞>C₃(0.51)  |  Immature: C₄<C∞<C₃  |  C∞<C₅(0.02): bed-load', ref:'Takahashi (2014) §2.2.2, Eq. 2.25; Bagnold (1966)' },
  velocity:        { title:'Mean Velocity U', eq:'U=(2/5dₚ)·√(g·sinθ/ai·sinαi)·[(C+(1-C)ρ/σ)^½·(C*/C)^⅓-1]·h^(3/2)', def:'Depth-averaged velocity — dilatant fluid model (Takahashi 2014). ai·sinαi=0.02 (Bagnold 1954 rigid-bed experiments).', range:'LOW <2  |  MODERATE 2–5  |  HIGH 5–8  |  CRITICAL >8 m/s', ref:'Takahashi (2014) §2.2.2, Eq. 2.29' },
  impactForce:     { title:'Impact Pressure P', eq:'P_dyn=½·ρ_T·U²  P_sta=ρ_T·g·h·cosθ', def:'Dynamic head + hydrostatic components. P_total used for SABO dam design. See §8 countermeasures.', range:'Masonry: ~10 kPa  |  RC check dam: 50–300 kPa  |  Steel net: total impulse', ref:'Takahashi (2014) §8; Hübl & Holzinger (2003)' },
  froudeNumber:    { title:'Froude Number Fr', eq:'Fr = U / √(g·h·cosθ)', def:'Ratio of inertial to gravitational forces. Governs flow regime.', range:'Fr>1: Supercritical  |  Fr<1: Subcritical', ref:'Iverson (1997)' },
  mixDensity:      { title:'Mixture Density ρ_mix', eq:'ρ_mix = C*·ρₛ+(1−C*)·ρ_f', def:'Mass-weighted density of solid + fluid phases.', range:'Typical: 1400–2200 kg/m³', ref:'Takahashi (2014)' },
  discharge:       { title:'Unit Discharge Q*', eq:'Q* = U·h  (m²/s)', def:'Volumetric flow per unit channel width.', range:'Alpine: 1–50  |  Major: >100 m²/s', ref:'Rickenmann (1999)' },
  iterations:      { title:'Iterations N', eq:'Xᵢ = X₀·(1+z·σ)  Box-Muller', def:'Number of random parameter sets.', range:'Min: 100  |  Standard: 500  |  High: 1000–2000', ref:'Box & Muller (1958)' },
  intensity:       { title:'Rainfall Intensity I', eq:'I_c = α·D^β', def:'Mean rainfall rate over event duration.', range:'Light: <2.5  |  Heavy: >7.5  |  Extreme: >50 mm/h', ref:'Caine (1980)' },
  duration:        { title:'Event Duration D', eq:'I_c = α·D^β  (β<0)', def:'Length of rainfall event. Longer → lower threshold.', range:'Flash: 0.25–3 h  |  Synoptic: 6–72 h', ref:'Caine (1980)' },
  alpha:           { title:'Scale Coefficient α', eq:'I = α·D^β', def:'Amplitude at D=1 h.', range:'Caine (1980) global: 14.82  |  Post-fire: 8.89', ref:'Caine (1980)' },
  beta:            { title:'Exponent β', eq:'I = α·D^β  (β negative)', def:'Decay rate with duration.', range:'Global: −0.39  |  Range: −0.20 to −0.70', ref:'Caine (1980)' },
  exceedance:      { title:'Exceedance Ratio', eq:'ER = I_obs / I_threshold', def:'Values >1 → threshold exceeded.', range:'<0.8 Stable  |  >1.0 Watch  |  >1.5 Critical', ref:'Guzzetti et al. (2008)' },
  safetyFactor:    { title:'Safety Factor SF', eq:'SF = 1 / ER = I_c / I_obs', def:'SF<1 means threshold exceeded.', range:'Design target: SF ≥ 1.5', ref:'Hungr et al. (2005)' },
  hornSlope:       { title:"Horn's Slope Algorithm", eq:'dz/dx = [(z_ne+2z_e+z_se)−(z_nw+2z_w+z_sw)]/(8Δx)', def:'8-neighbour finite-difference. Cardinals weighted 2×.', range:'Optimal DEM: 1–30 m resolution', ref:'Horn (1981)' }
};

// ═══════════════════════════════════════════════════════════
// TOOLTIP COMPONENT
// ═══════════════════════════════════════════════════════════
function Tip({ id, children }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const tip = TIPS[id];
  if (!tip) return <>{children}</>;
  const enter = e => {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ x: Math.min(r.left, window.innerWidth - 340), y: r.bottom + 8 });
    setShow(true);
  };
  return (
    <>
      <span onMouseEnter={enter} onMouseLeave={() => setShow(false)}
        style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        {children}
        <HelpCircle size={10} style={{ color: '#38bdf8', opacity: 0.75, flexShrink: 0 }} />
      </span>
      {show && (
        <div style={{
          position: 'fixed', left: pos.x, top: pos.y, zIndex: 9999,
          background: '#0a1628', border: '1px solid rgba(56,189,248,0.3)',
          borderRadius: 8, padding: '11px 13px', width: 300,
          boxShadow: '0 12px 40px rgba(0,0,0,0.7)', pointerEvents: 'none'
        }}>
          <div style={{ fontFamily: 'Space Mono,monospace', fontSize: 11, fontWeight: 700, color: '#38bdf8', marginBottom: 5 }}>{tip.title}</div>
          <div style={{ background: 'rgba(56,189,248,0.07)', border: '1px solid rgba(56,189,248,0.18)', borderRadius: 4, padding: '4px 8px', fontFamily: 'Space Mono,monospace', fontSize: 10, color: '#7dd3fc', marginBottom: 6 }}>{tip.eq}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.55, marginBottom: 5 }}>{tip.def}</div>
          {tip.range && <div style={{ fontSize: 10, color: '#64748b', borderTop: '1px solid #1e293b', paddingTop: 5, marginBottom: 4 }}><span style={{ color: '#f59e0b', fontWeight: 600 }}>Range: </span>{tip.range}</div>}
          <div style={{ fontSize: 10, color: '#475569', fontStyle: 'italic' }}>📖 {tip.ref}</div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// CALC PANEL
// ═══════════════════════════════════════════════════════════
function CalcPanel({ title, steps, color = '#0ea5e9' }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: `1px solid ${color}28`, borderRadius: 10, overflow: 'hidden', marginTop: 12 }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', background: `${color}09`, border: 'none', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color, fontFamily: 'Space Mono,monospace', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textAlign: 'left' }}>
        <BookOpen size={13} />{title}
        <span style={{ marginLeft: 'auto' }}>{open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}</span>
      </button>
      {open && (
        <div style={{ padding: '14px 16px', background: 'rgba(0,0,0,0.2)' }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, marginBottom: i < steps.length - 1 ? 14 : 0, paddingBottom: i < steps.length - 1 ? 14 : 0, borderBottom: i < steps.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: `${color}18`, border: `1px solid ${color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'Space Mono,monospace', fontSize: 10, color }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#cbd5e1', marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontFamily: 'Space Mono,monospace', fontSize: 11, whiteSpace: 'pre-wrap', background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.12)', borderRadius: 4, padding: '5px 8px', color: '#7dd3fc', marginBottom: (s.val || s.note) ? 5 : 0 }}>{s.eq}</div>
                {s.val && <div style={{ fontFamily: 'Space Mono,monospace', fontSize: 11, color, fontWeight: 700, marginBottom: s.note ? 3 : 0 }}>→ {s.val}</div>}
                {s.note && <div style={{ fontSize: 10, color: '#475569', lineHeight: 1.5 }}>{s.note}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CHART DEFAULTS
// ═══════════════════════════════════════════════════════════
const CHART_OPTS = {
  responsive: true, maintainAspectRatio: false, animation: { duration: 500 },
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { color: 'rgba(30,58,95,0.5)', borderColor: 'transparent' }, ticks: { color: '#475569', font: { family: 'Space Mono', size: 9 } } },
    y: { grid: { color: 'rgba(30,58,95,0.5)', borderColor: 'transparent' }, ticks: { color: '#475569', font: { family: 'Space Mono', size: 9 } } }
  }
};

function exportCSV(filename, headers, rows) {
  const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = filename; a.click();
}

const RISK_COLORS = { CRITICAL:'#ef4444', HIGH:'#f97316', MODERATE:'#f59e0b', LOW:'#22c55e', STABLE:'#94a3b8', WARNING:'#f97316', WATCH:'#f59e0b', ADVISORY:'#38bdf8' };

function RiskBadge({ level }) {
  const c = RISK_COLORS[level] || '#94a3b8';
  return <span className="risk-badge" style={{ background:`${c}18`, border:`1px solid ${c}50`, color:c }}><span style={{ width:6,height:6,borderRadius:'50%',background:c,display:'inline-block' }}/>{level}</span>;
}

function MetricCard({ label, value, unit, accentColor='var(--accent-blue)', tipId }) {
  const card = (
    <div className="metric-card animate-in" style={{ '--accent-color': accentColor }}>
      <div className="metric-label">{label}</div>
      <div className="metric-value" style={{ fontSize: 22, color: accentColor }}>{value}</div>
      {unit && <div className="metric-unit">{unit}</div>}
    </div>
  );
  return tipId ? <Tip id={tipId}>{card}</Tip> : card;
}

function LoadingBtn({ loading, label, loadingLabel='Computing…', onClick, icon }) {
  return (
    <button className="btn btn-primary btn-full" onClick={onClick} disabled={loading}>
      {loading ? <><span className="spinner"/>{loadingLabel}</> : <>{icon}{label}</>}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════
// LEARN PANELS PER MODULE
// ═══════════════════════════════════════════════════════════

function LearnTakahashi() {
  return (
    <div className="learn-layout">
      <div className="learn-hero">
        <div className="learn-hero-title">Takahashi Debris Flow Model — 2nd Edition (2014)</div>
        <div className="learn-hero-author">Tamotsu Takahashi · Professor Emeritus, Kyoto University · CRC Press/Balkema 2014</div>
        <div className="learn-hero-desc">
          The definitive reference for debris flow mechanics, prediction and countermeasure design.
          The 2014 second edition introduces the generalized theory for inertial debris flows (§2.7),
          the Newtonian fluid model for viscous flows (§2.8), complete initiation criteria (§3.1),
          updated SABO design equations (§8), and new sediment runoff models (Ch. 6).
          All physics in this suite is derived directly from these equations.
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><Activity size={15} color="#0ea5e9"/><span className="learn-card-title">Core Mechanics — Dilatant Fluid Model (§2.2.2)</span></div>
        <div className="learn-card-body">
          <div className="learn-section">
            <div className="learn-section-title">Bagnold Dispersive Pressure (§2.2.2, Eq. 2.18–2.19)</div>
            <div className="learn-text">In the grain-inertia regime, rapid inter-particle collisions generate a normal (dispersive) pressure and shear stress proportional to the square of the shear rate. Bagnold (1954) derived experimentally:</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Bagnold Constitutive Equations (Eq. 2.18–2.19)</div>
              p_c = a_i · cos(α_i) · σ · λ² · d_p² · (du/dz)²<br/>
              τ_c = tan(α_i) · p_c<br/><br/>
              a_i = 0.042 (Bagnold 1954 experiments)<br/>
              tan(α_i) = 0.32  (α_i = 17.8°)<br/>
              λ = linear concentration = d_p / (spacing between particles)<br/>
              Relationship to volume concentration C:<br/>
              C = C* / (1/λ + 1)³     C* ≈ 0.65 for natural grains
            </div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Equilibrium Concentration C∞ (§2.2.2, Eq. 2.25)</div>
            <div className="learn-text">The equilibrium (or "fully-developed") concentration where gravity drive equals dispersive resistance. The flow continuously erodes or deposits sediment until this state is reached.</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Takahashi (1977) — verified experimentally; Eq. 2.25</div>
              C∞ = ρ · tan θ / [(σ - ρ)(tan φ - tan θ)]<br/><br/>
              Requires: tan θ &lt; tan φ  (slope below angle of repose)<br/>
              Maximum experimentally: C∞ ≤ 0.9 · C*<br/><br/>
              Note: The original 1981 form used (σ-ρ)/ρ · 0.85 as denominator.
              The 2014 2nd edition corrects this to explicitly use tan φ instead of
              the constant 0.85, making the formula applicable to any grain type.
            </div>
            <div className="learn-highlight">
              Discharge amplification (Eq. 3.23): q_debris/q₀ = C*/(C* - C∞)<br/>
              A 20° slope can amplify water discharge by 4–8× as the flow erodes the bed.
              This explains why small rainstorm runoffs become catastrophic debris flows.
            </div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Mean Velocity — Dilatant Fluid Model (§2.2.2, Eq. 2.29)</div>
            <div className="learn-text">Integration of the stress balance equations for a wide rectangular channel with uniform concentration. The (C*/C)^(1/3) term represents the dilatancy factor — how much particles must "climb over" each other.</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Eq. 2.29 — Cross-section mean velocity</div>
              U = (2/5d_p) · √(g·sinθ / a_i·sinα_i) · [(C + (1-C)ρ/σ)^½ · (C*/C)^⅓ - 1] · h^(3/2)<br/><br/>
              a_i · sin(α_i) = 0.02  (Bagnold 1954 rigid-bed, Takahashi 1977)<br/>
              Simplified unit-width discharge (Eq. 2.30):<br/>
              q_T = U · h = (2/5) · R · h^(5/2) · sin^½θ     R ≈ 5.4 m^(-½)s^(-1)
            </div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Two-Phase Mixture Theory (§2.3, Eqs. 2.3–2.4)</div>
            <div className="learn-text">The rigorous foundation: pressure and shear stress balance equations for the solid and fluid phases combined:</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Stress balance equations (Takahashi 1991)</div>
              P_ds + P_f = (σ-ρ)·g·cosθ · ∫[z→h] C dz           [Eq. 2.3]<br/>
              T_ds + T_f = g·sinθ · ∫[z→h] ρ_T dz               [Eq. 2.4]<br/><br/>
              P_ds = dispersive pressure in solid phase<br/>
              P_f  = dynamic fluid pressure (excess over hydrostatic)<br/>
              T_ds = shear stress in solid phase<br/>
              T_f  = shear stress in fluid phase<br/>
              ρ_T  = (σ-ρ)C + ρ  = mixture density             [Eq. 2.37]
            </div>
          </div>
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><Layers size={15} color="#a855f7"/><span className="learn-card-title">Flow Classification System (§2.5, §2.6)</span></div>
        <div className="learn-card-body">
          <div className="learn-text">Takahashi (2014) classifies debris flows using a ternary diagram (Figure 2.26) with three axes: Bagnold number Ba, Reynolds number Re, and relative depth h/d_p. The dominant stress determines flow type.</div>
          <div className="learn-section">
            <div className="learn-section-title">Concentration Thresholds (§2.5, Bagnold 1966; Takahashi 1982)</div>
          </div>
          <table className="learn-threshold-table">
            <thead><tr><th>Symbol</th><th>Value</th><th>Meaning</th></tr></thead>
            <tbody>
              <tr><td>C*</td><td>≈ 0.65</td><td>Maximum packing — no flow possible above this</td></tr>
              <tr><td>C₁</td><td>≈ 0.604</td><td>Concentration after shearing (loosest stable packing)</td></tr>
              <tr><td>C₂</td><td>≈ 0.56</td><td>Upper quasi-static limit — skeletal contact stress begins</td></tr>
              <tr><td>C₃</td><td>≈ 0.51</td><td>Lower quasi-static limit — collision stress takes over below this</td></tr>
              <tr><td>C₄</td><td>≈ 0.20</td><td>Lower stony DF limit — particles disperse full depth (Takahashi 1982)</td></tr>
              <tr><td>C₅</td><td>≈ 0.02</td><td>Lower immature DF limit — transition to individual bed-load transport</td></tr>
            </tbody>
          </table>
          <div className="learn-section" style={{marginTop:14}}>
            <div className="learn-section-title">Bagnold Number Ba (§2.5, Eq. 2.127)</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Classification criterion</div>
              Ba = σ·d_p²·(du/dz) / [(C*/C)^(1/3) - 1]^(1/2) / μ<br/><br/>
              Ba &gt; 450  →  Inertial range: τ_c ≫ τ_μ  (collision-dominated = stony type)<br/>
              Ba &lt; 40   →  Viscous range:  τ_μ ≫ τ_c  (viscous = fine-grained type)<br/>
              h/d_p &gt; 30 and Re &gt; 1500  →  Turbulent-muddy type
            </div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Flow Type Characteristics</div>
          </div>
          <div className="learn-flow-type">
            <div className="learn-flow-card" style={{'--accent-color':'#22d3ee'}}>
              <div className="learn-flow-name" style={{color:'#22d3ee'}}>Stony (Mature)</div>
              <div className="learn-flow-desc">C∞ &gt; 0.51 (C₃). Ba &gt; 450, h/d_p &lt; 10. Bagnold dispersive pressure dominant — inter-particle collisions support entire sediment load. Dilatant fluid model (Eq. 2.22–2.29). Boulder front, inverse grading. Kamikamihorizawa, Japan — archetypal example.</div>
            </div>
            <div className="learn-flow-card" style={{'--accent-color':'#818cf8'}}>
              <div className="learn-flow-name" style={{color:'#818cf8'}}>Immature</div>
              <div className="learn-flow-desc">C₅ &lt; C∞ &lt; C₄ (0.02–0.20). Two-layer structure: lower particle-collision layer + upper plain-water layer (Takahashi 1982). Velocity: U/u* = 0.4–0.7 · h/d_p (Eq. 2.143). Occurs on 4°–10° slopes transitioning from stony to bed-load.</div>
            </div>
            <div className="learn-flow-card" style={{'--accent-color':'#f59e0b'}}>
              <div className="learn-flow-name" style={{color:'#f59e0b'}}>Viscous</div>
              <div className="learn-flow-desc">Ba &lt; 40, Re &lt; 500. Newtonian/Bingham fluid model. High-density fine slurry (ρ_T = 1.7–2.3 t/m³). Jiangjia Gully, China — apparent viscosity ≈ 200 Pa·s. Laminar parabolic velocity profile. Stops by lateral narrowing, not yield strength — key diagnostic.</div>
            </div>
            <div className="learn-flow-card" style={{'--accent-color':'#10b981'}}>
              <div className="learn-flow-name" style={{color:'#10b981'}}>Turbulent-Muddy</div>
              <div className="learn-flow-desc">h/d_p &gt; 30, Re &gt; 1500. Turbulent suspension supports small particles (d_p &lt; 1–10 mm). Velocity: U/u* = 5–20 (approaches plain water). Nojiri River, Japan — classic case. Resistance asymptotes to Manning equation with increasing h/d_p.</div>
            </div>
          </div>
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><CloudRain size={15} color="#ef4444"/><span className="learn-card-title">Initiation Mechanics (§3.1)</span></div>
        <div className="learn-card-body">
          <div className="learn-section">
            <div className="learn-section-title">Critical Slope Thresholds (§3.1.1, Eqs. 3.4 and 3.8)</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">θ₁ — Minimum slope for stony DF initiation (Eq. 3.8)</div>
              tan θ₁ = C*(σ-ρ)·tan φ / [C*(σ-ρ) + ρ(1 + 1/k)]<br/><br/>
              k = ratio of surface runoff depth to sediment layer depth ≈ 0.7<br/>
              → θ₁ ≈ 14.5°–15° for typical granitic gravel (C*=0.65, tan φ=0.8)<br/>
              Verified by laboratory experiments (Takahashi 1977) and field data<br/><br/>
              <div className="learn-eq-label">θ₂ — Maximum stable slope / deposition angle (Eq. 3.4)</div>
              tan θ₂ = C*(σ-ρ)·tan φ / [C*(σ-ρ) + ρ]<br/><br/>
              → θ₂ ≈ 18°–22° for typical conditions<br/>
              Above θ₂ the entire saturated bed fails — catastrophic full-depth slide
            </div>
            <div className="learn-highlight">
              Slope zones: θ &lt; θ₁ → deposition · θ₁ &lt; θ &lt; θ₂ → active DF transport · θ &gt; θ₂ → initiation/bed failure
            </div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Debris Flow Development (§3.1.2, Eqs. 3.16–3.23)</div>
            <div className="learn-text">Once initiated, debris flow grows as it erodes the bed. The thickness of bed that becomes unstable is:</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Unstable layer thickness (Eq. 3.16 / 3.12)</div>
              a_c / d_p = C∞·h_o / [(C* - C∞)·d_p]<br/><br/>
              The concentration grows asymptotically toward C∞ (Eq. 3.20–3.21).
              The discharge amplification ratio (Eq. 3.23):<br/>
              q_t / q_o = C* / (C* - C∞)<br/><br/>
              Example: θ=20°, σ=2650, tanφ=0.8 → C∞ ≈ 0.45<br/>
              → q_t/q_o = 0.65/(0.65-0.45) = 3.25×  amplification
            </div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Settling Velocities (§2.5, Eqs. 2.128–2.130)</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Regime-dependent settling</div>
              Newton regime (d_p ≥ 1 cm):  w_s = 1.82·√[(σ-ρ)/ρ · g · d_p]<br/>
              Stokes regime (d_p &lt; 1 mm): w_s = g·(σ-ρ)·d_p² / 18μ<br/><br/>
              Suspension condition (Eq. 2.129):  h/d_p &gt; 3.31·(σ/ρ - 1)/sinθ<br/>
              → For θ=10°, σ=2650: h/d_p &gt; 32 needed for turbulent suspension
            </div>
          </div>
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><Mountain size={15} color="#f59e0b"/><span className="learn-card-title">SABO Design — Impact Pressures (§4, §8)</span></div>
        <div className="learn-card-body">
          <div className="learn-section">
            <div className="learn-section-title">Impact Pressure Components</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">SABO check dam / barrier design</div>
              P_dynamic = ½ · ρ_T · U²          [Pa]  — velocity head pressure<br/>
              P_static  = ρ_T · g · h · cosθ    [Pa]  — hydrostatic component<br/>
              P_total   = P_static + P_dynamic<br/><br/>
              ρ_T = σ·C∞ + (1-C∞)·ρ            [Eq. 2.37]<br/><br/>
              Design guidance (Hübl &amp; Holzinger 2003; Swiss guidelines):<br/>
              Unreinforced masonry: failure above ~10 kPa<br/>
              Reinforced concrete check dam: design for 50–300 kPa<br/>
              Steel ring-net: designed for total impulse = ρ_T·U²·h·width
            </div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Boulder Accumulation at Front (§4.2)</div>
            <div className="learn-text">The Takahashi (1980) theory explains why large boulders concentrate at the debris flow snout. The dispersive pressure from the dense body behind pushes larger particles forward — "inverse grading." The front acts as a moving dam, and the impact force is dominated by boulder size, not average flow density.</div>
          </div>
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><BookOpen size={15} color="#475569"/><span className="learn-card-title">Key References</span></div>
        <div className="learn-card-body">
          <ul className="learn-ref-list">
            <li className="learn-ref-item"><b>Takahashi, T. (2014).</b> Debris Flow: Mechanics, Prediction and Countermeasures, 2nd ed. CRC Press/Balkema. <em>— Primary reference for all equations in this module</em></li>
            <li className="learn-ref-item"><b>Takahashi, T. (1977).</b> A mechanism of occurrence of mud-debris flows and their characteristics in motion. DPRI Annals 20B-2, 405–435. <em>— Original dilatant fluid model and C∞ derivation</em></li>
            <li className="learn-ref-item"><b>Takahashi, T. (1981).</b> Debris flow. Annual Review of Fluid Mechanics, 13, 57–77. <em>— Classic review article; equilibrium concentration formula</em></li>
            <li className="learn-ref-item"><b>Takahashi, T. (1982).</b> On the immature debris flow. Proc. IAHR Congress, 14, 399–404. <em>— Two-layer immature flow model; C₄ threshold (0.20)</em></li>
            <li className="learn-ref-item"><b>Bagnold, R.A. (1954).</b> Experiments on a gravity-free dispersion of large solid spheres in a Newtonian fluid. Proc. Royal Society 225A, 49–63. <em>— Dispersive pressure foundation; ai, tan αi constants</em></li>
            <li className="learn-ref-item"><b>Takahashi, T. &amp; Tsujimoto, H. (1997).</b> Mechanics of granular flow in inclined chute. J. Hydraulic, Coastal and Environmental Engineering 565, 57–71. <em>— Kinetic + collision stress model; Eqs. 2.78, 2.84–2.85</em></li>
            <li className="learn-ref-item"><b>Takahashi, T. &amp; Satofuka, Y. (2002).</b> Generalized theory for inertial debris flows. INTERPRAEVENT 2002. <em>— Hybrid-type flow; Eqs. 2.152–2.171</em></li>
            <li className="learn-ref-item"><b>Iverson, R.M. (1997).</b> The physics of debris flows. Reviews of Geophysics 35(3), 245–296. <em>— Coulomb mixture theory; pore pressure analysis</em></li>
            <li className="learn-ref-item"><b>Iverson, R.M. &amp; Denlinger, R.P. (2001).</b> Flow of variably fluidized granular masses across three-dimensional terrain. J. Geophys. Res. 106, 537–552.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function LearnMonteCarlo() {
  return (
    <div className="learn-layout">
      <div className="learn-hero">
        <div className="learn-hero-title">Monte Carlo Probabilistic Analysis</div>
        <div className="learn-hero-author">Box & Muller (1958) · Metropolis & Ulam (1949)</div>
        <div className="learn-hero-desc">
          Debris flow parameters carry significant uncertainty — slope angle from DEM resolution, grain size from sampling, flow depth from limited gauge data.
          Monte Carlo simulation propagates this uncertainty through the physics model to produce a probability distribution of outcomes rather than a single deterministic value.
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><Zap size={15} color="#a855f7"/><span className="learn-card-title">Box-Muller Transform</span></div>
        <div className="learn-card-body">
          <div className="learn-section">
            <div className="learn-section-title">Generating Gaussian Random Numbers</div>
            <div className="learn-text">Computers generate uniform random numbers natively. The Box-Muller transform converts two independent uniform variates into a pair of independent standard normal variates:</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Box-Muller Transform (1958)</div>
              u₁, u₂ ~ Uniform(0, 1)  — independent<br/><br/>
              z₁ = √(−2 · ln u₁) · cos(2π · u₂)<br/>
              z₂ = √(−2 · ln u₁) · sin(2π · u₂)<br/><br/>
              Both z₁ and z₂ ~ N(0, 1)  (standard normal)
            </div>
            <div className="learn-text">In our implementation, z₁ alone is used per iteration. The result is clamped to ±30% of the nominal value to avoid physically impossible parameter combinations.</div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Parameter Perturbation</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Perturbed Input</div>
              Xᵢ = X₀ · clamp(1 + z · σ,  0.70,  1.30)<br/><br/>
              X₀ = nominal input value<br/>
              σ  = coefficient of variation (uncertainty level)
            </div>
            <div className="learn-vars-grid">
              <span className="learn-var-sym">Slope angle</span><span className="learn-var-def">σ = 8% — DEM resolution error, survey uncertainty</span>
              <span className="learn-var-sym">Grain diameter</span><span className="learn-var-def">σ = 12% — natural grain-size variability</span>
              <span className="learn-var-sym">Flow depth</span><span className="learn-var-def">σ = 15% — largest uncertainty; field measurement difficulty</span>
              <span className="learn-var-sym">Particle density</span><span className="learn-var-def">σ = 4% — mineral composition variation</span>
              <span className="learn-var-sym">Fluid density</span><span className="learn-var-def">σ = 3% — sediment load in water</span>
            </div>
          </div>
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><BarChart2 size={15} color="#0ea5e9"/><span className="learn-card-title">Statistical Interpretation</span></div>
        <div className="learn-card-body">
          <div className="learn-section">
            <div className="learn-section-title">Percentile-Based Design Values</div>
            <div className="learn-text">Engineering design uses percentile values rather than means, because the <em>consequences</em> of underestimation are asymmetric:</div>
            <table className="learn-threshold-table">
              <thead><tr><th>Percentile</th><th>Use Case</th><th>Meaning</th></tr></thead>
              <tbody>
                <tr><td>P5 (5th)</td><td>Best-case scenario</td><td>95% of simulations exceed this value</td></tr>
                <tr><td>P50 (median)</td><td>Most likely outcome</td><td>Half of simulations above, half below</td></tr>
                <tr><td>P95 (95th)</td><td>Design for infrastructure</td><td>Only 5% of simulations exceed this</td></tr>
                <tr><td>Max</td><td>Worst-case / nuclear option</td><td>Maximum simulated outcome</td></tr>
              </tbody>
            </table>
            <div className="learn-text" style={{marginTop:10}}>For debris flow hazard assessment, the <strong>P95 velocity</strong> is typically used for structural design, while P50 is used for evacuation route planning and early warning thresholds.</div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Central Limit Theorem Foundation</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Sample Statistics from N Runs</div>
              μ = (1/N) · Σ Uᵢ          (sample mean)<br/>
              σ = √[(1/N) · Σ(Uᵢ − μ)²] (sample std. dev.)<br/>
              SE = σ / √N                 (standard error)<br/><br/>
              For N=500: SE ≈ σ/22 — very stable estimates
            </div>
          </div>
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><BookOpen size={15} color="#475569"/><span className="learn-card-title">Key References</span></div>
        <div className="learn-card-body">
          <ul className="learn-ref-list">
            <li className="learn-ref-item"><b>Box, G.E.P. & Muller, M.E. (1958).</b> A note on the generation of random normal deviates. Annals of Mathematical Statistics, 29(2), 610–611.</li>
            <li className="learn-ref-item"><b>Metropolis, N. & Ulam, S. (1949).</b> The Monte Carlo method. Journal of the American Statistical Association, 44(247), 335–341.</li>
            <li className="learn-ref-item"><b>Christian, J.T. & Baecher, G.B. (2002).</b> The point-estimate method with large numbers of variables. Int. J. Numer. Anal. Meth. Geomech., 26, 1515–1529.</li>
            <li className="learn-ref-item"><b>Haneberg, W.C. (2004).</b> A rational probabilistic method for spatially distributed landslide hazard assessment. Environmental & Engineering Geoscience, 10(1), 27–43.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function LearnDEM() {
  return (
    <div className="learn-layout">
      <div className="learn-hero">
        <div className="learn-hero-title">DEM Terrain Analysis</div>
        <div className="learn-hero-author">Horn (1981) · Zevenbergen & Thorne (1987)</div>
        <div className="learn-hero-desc">
          Digital Elevation Models encode terrain morphology as a raster grid of elevation values. Extracting slope from this data
          is the first step in any debris flow hazard assessment — it drives every physics calculation downstream.
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><Mountain size={15} color="#10b981"/><span className="learn-card-title">Horn's (1981) Finite-Difference Slope Algorithm</span></div>
        <div className="learn-card-body">
          <div className="learn-section">
            <div className="learn-section-title">The 3×3 Kernel</div>
            <div className="learn-text">For every interior cell (x,y) in the raster, a 3×3 neighbourhood of elevation values is extracted:</div>
            <div className="learn-eq-block">
              z_nw  z_n   z_ne<br/>
              z_w   z_c   z_e      ← 8 neighbours around center cell z_c<br/>
              z_sw  z_s   z_se
            </div>
            <div className="learn-text">Horn's key innovation: cardinal neighbours (N, S, E, W) are weighted <strong>twice as heavily</strong> as diagonal neighbours, reducing noise compared to simpler 4-neighbour finite differences.</div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Gradient Computation</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">East-West Derivative</div>
              dz/dx = [(z_ne + 2·z_e + z_se) − (z_nw + 2·z_w + z_sw)] / (8·Δx)<br/><br/>
              <div className="learn-eq-label">North-South Derivative</div>
              dz/dy = [(z_sw + 2·z_s + z_se) − (z_nw + 2·z_n + z_ne)] / (8·Δy)<br/><br/>
              <div className="learn-eq-label">Slope Angle</div>
              θ = arctan(√[(dz/dx)² + (dz/dy)²])   [degrees]
            </div>
            <div className="learn-vars-grid">
              <span className="learn-var-sym">Δx, Δy</span><span className="learn-var-def">Pixel dimensions in metres (from GeoTIFF georeference)</span>
              <span className="learn-var-sym">dz/dx</span><span className="learn-var-def">East-west rate of elevation change</span>
              <span className="learn-var-sym">dz/dy</span><span className="learn-var-def">North-south rate of elevation change</span>
            </div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">DEM Quality Requirements</div>
            <table className="learn-threshold-table">
              <thead><tr><th>Resolution</th><th>Source</th><th>Suitability</th></tr></thead>
              <tbody>
                <tr><td>0.25–1 m</td><td>LiDAR (airborne/UAV)</td><td>Excellent — individual channel morphology</td></tr>
                <tr><td>5–10 m</td><td>LiDAR regional / photogrammetry</td><td>Good — watershed-scale analysis</td></tr>
                <tr><td>30 m</td><td>SRTM, ASTER GDEM</td><td>Adequate for regional screening</td></tr>
                <tr><td>&gt;90 m</td><td>SRTM 3 Arc-Second</td><td>Too coarse for most applications</td></tr>
              </tbody>
            </table>
            <div className="learn-highlight">UTM or other projected coordinate system required — geographic (lat/lon) coordinates will produce incorrect slope values because Δx varies with latitude.</div>
          </div>
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><Layers size={15} color="#f59e0b"/><span className="learn-card-title">Geohazard Zone Classification</span></div>
        <div className="learn-card-body">
          <div className="learn-section">
            <div className="learn-section-title">Slope-Based Classification Scheme</div>
            <div className="learn-text">Slope thresholds are based on Takahashi (2014) and Hungr et al. (2014) equilibrium analysis:</div>
            <table className="learn-threshold-table">
              <thead><tr><th>Zone</th><th>Slope Range</th><th>Physical Significance</th></tr></thead>
              <tbody>
                <tr><td style={{color:'#7c3aed',fontWeight:700}}>Bedrock / Cliff</td><td>&gt;45°</td><td>Rock-fall and cliff collapse source; slope too steep for debris accumulation</td></tr>
                <tr><td style={{color:'#ef4444',fontWeight:700}}>Initiation</td><td>30–45°</td><td>Prime debris flow source zone; C* is high, flows begin and accelerate</td></tr>
                <tr><td style={{color:'#f59e0b',fontWeight:700}}>Transport</td><td>15–30°</td><td>Active scour corridor; flow is mobile, eroding or depositing depending on C*</td></tr>
                <tr><td style={{color:'#10b981',fontWeight:700}}>Deposition</td><td>2–15°</td><td>Debris fan / alluvial zone; flow decelerates and deposits sediment</td></tr>
                <tr><td style={{color:'#94a3b8',fontWeight:700}}>Stable</td><td>&lt;2°</td><td>Low mobility; flows have deposited; minimal hazard except from upstream</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><BookOpen size={15} color="#475569"/><span className="learn-card-title">Key References</span></div>
        <div className="learn-card-body">
          <ul className="learn-ref-list">
            <li className="learn-ref-item"><b>Horn, B.K.P. (1981).</b> Hill shading and the reflectance map. Proceedings of the IEEE, 69(1), 14–47.</li>
            <li className="learn-ref-item"><b>Zevenbergen, L.W. & Thorne, C.R. (1987).</b> Quantitative analysis of land surface topography. Earth Surface Processes and Landforms, 12(1), 47–56.</li>
            <li className="learn-ref-item"><b>OpenTopography (2024).</b> SRTM 1 Arc-Second Global. https://opentopography.org</li>
            <li className="learn-ref-item"><b>Dietrich, W.E. et al. (1995).</b> Analysis of erosion thresholds, channel networks, and landscape morphology using a digital terrain model. Journal of Geology, 100, 259–278.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function LearnRainfall() {
  return (
    <div className="learn-layout">
      <div className="learn-hero">
        <div className="learn-hero-title">Rainfall Intensity-Duration Thresholds</div>
        <div className="learn-hero-author">Caine (1980) · Guzzetti et al. (2008) · Brunetti et al. (2010)</div>
        <div className="learn-hero-desc">
          Empirical observation shows that debris flow initiation correlates with rainfall intensity AND duration as a power-law relationship.
          Caine (1980) established the global baseline used by this suite. Post-fire correction accounts for the dramatically reduced
          threshold observed after wildfire destroys vegetative root cohesion.
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><CloudRain size={15} color="#f59e0b"/><span className="learn-card-title">The Power-Law Threshold</span></div>
        <div className="learn-card-body">
          <div className="learn-section">
            <div className="learn-section-title">Caine (1980) Global Baseline</div>
            <div className="learn-text">Caine compiled worldwide records of rainfall-triggered shallow landslides and debris flows, fitting a lower-bound power-law envelope to all data points:</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Caine (1980) — Geografiska Annaler</div>
              I = 14.82 · D^(−0.39)    [I in mm/h, D in hours]<br/><br/>
              Valid range: D = 0.1 to 500 hours<br/>
              Global lower bound — events plotting above this curve initiated flows
            </div>
            <div className="learn-text">The negative exponent (β = −0.39) means that for longer storm durations, a <em>lower</em> intensity suffices to trigger flows — a 24-hour storm needs far less intensity than a 1-hour burst to be equally dangerous.</div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Regional Variations in α and β</div>
            <table className="learn-threshold-table">
              <thead><tr><th>Region</th><th>α</th><th>β</th><th>Source</th></tr></thead>
              <tbody>
                <tr><td>Global (baseline)</td><td>14.82</td><td>−0.39</td><td>Caine (1980)</td></tr>
                <tr><td>Italy (all)</td><td>19.0</td><td>−0.50</td><td>Brunetti et al. (2010)</td></tr>
                <tr><td>European Alps</td><td>17.8</td><td>−0.45</td><td>Guzzetti et al. (2008)</td></tr>
                <tr><td>Post-fire (empirical)</td><td>8.89</td><td>−0.39</td><td>Caine × 0.6; Staley et al. (2013)</td></tr>
              </tbody>
            </table>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Post-Fire Threshold Reduction</div>
            <div className="learn-text">Wildfire dramatically lowers debris flow thresholds through three mechanisms:</div>
            <div className="learn-vars-grid">
              <span className="learn-var-sym">Root loss</span><span className="learn-var-def">Fire destroys root cohesion holding soil together — immediate effect</span>
              <span className="learn-var-sym">Hydrophobicity</span><span className="learn-var-def">Combustion products create water-repellent layer → increased runoff</span>
              <span className="learn-var-sym">Ash matrix</span><span className="learn-var-def">Fine ash reduces infiltration, mobilises as viscous slurry</span>
            </div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Post-Fire Correction (Cannon & Gartner 2005)</div>
              α_postfire = α_normal × 0.6<br/>
              (40% reduction in critical threshold amplitude)
            </div>
          </div>
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><AlertTriangle size={15} color="#ef4444"/><span className="learn-card-title">Risk Classification Scheme</span></div>
        <div className="learn-card-body">
          <table className="learn-threshold-table">
            <thead><tr><th>Risk Level</th><th>Exceedance Ratio</th><th>Safety Factor</th><th>Action</th></tr></thead>
            <tbody>
              <tr><td style={{color:'#ef4444',fontWeight:700}}>CRITICAL</td><td>ER &gt; 1.5</td><td>SF &lt; 0.67</td><td>Immediate evacuation; close roads</td></tr>
              <tr><td style={{color:'#f97316',fontWeight:700}}>WARNING</td><td>1.2–1.5</td><td>0.67–0.83</td><td>Alert emergency services; restrict access</td></tr>
              <tr><td style={{color:'#f59e0b',fontWeight:700}}>WATCH</td><td>1.0–1.2</td><td>0.83–1.00</td><td>Heightened monitoring; prepare response</td></tr>
              <tr><td style={{color:'#38bdf8',fontWeight:700}}>ADVISORY</td><td>0.8–1.0</td><td>1.00–1.25</td><td>Caution; conditions deteriorating</td></tr>
              <tr><td style={{color:'#94a3b8',fontWeight:700}}>STABLE</td><td>&lt; 0.8</td><td>&gt; 1.25</td><td>Normal conditions</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><BookOpen size={15} color="#475569"/><span className="learn-card-title">Key References</span></div>
        <div className="learn-card-body">
          <ul className="learn-ref-list">
            <li className="learn-ref-item"><b>Caine, N. (1980).</b> The rainfall intensity-duration control of shallow landslides and debris flows. Geografiska Annaler, 62A(1–2), 23–27. <em>— Foundational paper</em></li>
            <li className="learn-ref-item"><b>Guzzetti, F. et al. (2008).</b> The rainfall intensity-duration control of landslides and debris flows. Geomorphology, 102(3–4), 636–650.</li>
            <li className="learn-ref-item"><b>Brunetti, M.T. et al. (2010).</b> Rainfall thresholds for the possible occurrence of landslides in Italy. Nat. Hazards Earth Syst. Sci., 10(3), 447–458.</li>
            <li className="learn-ref-item"><b>Staley, D.M. et al. (2013).</b> Objective definition of rainfall intensity-duration thresholds for post-fire flash floods. Geomorphology, 191, 70–79.</li>
            <li className="learn-ref-item"><b>Cannon, S.H. & Gartner, J.E. (2005).</b> Wildfire-related debris flow from a hazards perspective. In Debris-flow Hazards and Related Phenomena. Springer.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function LearnWeather() {
  return (
    <div className="learn-layout">
      <div className="learn-hero">
        <div className="learn-hero-title">Real-Time Hazard Monitoring</div>
        <div className="learn-hero-author">Aleotti (2004) · Segoni et al. (2014) · OpenWeatherMap API</div>
        <div className="learn-hero-desc">
          The Live Monitor integrates real-time precipitation data with the Caine (1980) threshold engine, providing
          continuous automated hazard assessment for instrumented sites. Modern early-warning systems combine
          threshold-based triggers with rainfall radar nowcasting for 0–6 hour lead times.
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><Radio size={15} color="#38bdf8"/><span className="learn-card-title">Early Warning System Architecture</span></div>
        <div className="learn-card-body">
          <div className="learn-section">
            <div className="learn-section-title">Threshold-Based Alert Trigger</div>
            <div className="learn-text">The system compares real-time observed intensity to the Caine (1980) critical threshold at 1-hour duration. An alert fires when:</div>
            <div className="learn-eq-block">
              I_observed ≥ I_critical (1h)<br/>
              I_critical (1h) = α · 1^β = α · 1 = α = 14.82 mm/h  (global)<br/><br/>
              Post-fire: I_critical (1h) = 8.89 mm/h
            </div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Operational Monitoring Protocols</div>
            <table className="learn-threshold-table">
              <thead><tr><th>Parameter</th><th>Value / Guidance</th></tr></thead>
              <tbody>
                <tr><td>Polling interval</td><td>10 minutes (this suite) — standard for operational monitoring</td></tr>
                <tr><td>Minimum gauge record</td><td>≥5 years for reliable threshold calibration</td></tr>
                <tr><td>Antecedent moisture</td><td>Pre-wet soils can lower effective threshold 20–40%</td></tr>
                <tr><td>Spatial representativeness</td><td>One gauge represents ~5 km² in mountainous terrain</td></tr>
                <tr><td>Response time</td><td>Allow 30–60 min for evacuation before estimated flow arrival</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><BookOpen size={15} color="#475569"/><span className="learn-card-title">Key References</span></div>
        <div className="learn-card-body">
          <ul className="learn-ref-list">
            <li className="learn-ref-item"><b>Aleotti, P. (2004).</b> A warning system for rainfall-induced shallow failures. Engineering Geology, 73(3–4), 247–265.</li>
            <li className="learn-ref-item"><b>Segoni, S. et al. (2014).</b> Analysis of satellite-derived rainfall data for the derivation of rainfall thresholds. Nat. Hazards Earth Syst. Sci., 14, 2681–2692.</li>
            <li className="learn-ref-item"><b>Montrasio, L. & Valentino, R. (2008).</b> A model for triggering mechanisms of shallow landslides. Nat. Hazards Earth Syst. Sci., 8(5), 1149–1159.</li>
            <li className="learn-ref-item"><b>OpenWeatherMap (2024).</b> Current Weather Data API Documentation. https://openweathermap.org/current</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ANALYZE MODULES (unchanged physics, same as v2.2)
// ═══════════════════════════════════════════════════════════

function AnalyzeTakahashi() {
  const [params, setParams] = useState({
    slopeAngle:      { value:22,    label:'Slope Angle θ',          unit:'°',     tipId:'slopeAngle',      min:1,    max:45,   step:0.5  },
    grainDiameter:   { value:50,    label:'Grain Diameter dₚ',      unit:'mm',    tipId:'grainDiameter',   min:1,    max:500,  step:1    },
    waterDepth:      { value:2,     label:'Flow Depth h',            unit:'m',     tipId:'waterDepth',      min:0.1,  max:20,   step:0.1  },
    particleDensity: { value:2650,  label:'Particle Density σ',      unit:'kg/m³', tipId:'particleDensity', min:1500, max:3200, step:50   },
    fluidDensity:    { value:1000,  label:'Fluid Density ρ',         unit:'kg/m³', tipId:'fluidDensity',    min:900,  max:1500, step:10   },
    tanPhi:          { value:0.75,  label:'tan φ (friction angle)',   unit:'',      tipId:'froudeNumber',    min:0.3,  max:1.2,  step:0.01 },
    Cstar:           { value:0.65,  label:'C* (max packing)',         unit:'',      tipId:'concentration',   min:0.4,  max:0.8,  step:0.01 },
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const setVal = (k,v) => setParams(p => ({...p,[k]:{...p[k],value:parseFloat(v)}}));
  const run = async () => {
    setLoading(true); setError(null);
    try {
      const body = Object.fromEntries(Object.entries(params).map(([k,v])=>[k,v.value]));
      const data = await (await fetch(`${API}/analyze`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)})).json();
      if(data.error) throw new Error(data.error);
      setResult(data);
    } catch(e){setError(e.message);}
    setLoading(false);
  };
  const rc = result ? (RISK_COLORS[result.hazardLevel]||'#94a3b8') : null;

  // ── Step-by-step calc breakdown (Takahashi 2014) ──────────────────────
  const calcSteps = result ? (() => {
    const θ = params.slopeAngle.value;
    const σ = params.particleDensity.value;
    const ρ = params.fluidDensity.value;
    const h = params.waterDepth.value;
    const dp = (params.grainDiameter.value / 1000).toFixed(4);
    const φ = params.tanPhi.value;
    const Cstar = params.Cstar.value;
    const θr = (θ * Math.PI / 180);
    const tanθ = Math.tan(θr).toFixed(4);
    const sinθ = Math.sin(θr).toFixed(4);
    const cosθ = Math.cos(θr).toFixed(4);
    return [
      { label:'Critical slope θ₁ (min initiation)',
        eq:   'tanθ₁ = C*(σ-ρ)tanφ / [C*(σ-ρ)+ρ(1+1/k)],  k=0.7',
        val:  `θ₁ = ${result.minInitiationSlopeDeg}°  — slopes steeper than this can generate stony debris flow` },
      { label:'Critical slope θ₂ (max stable bed)',
        eq:   'tanθ₂ = C*(σ-ρ)tanφ / [C*(σ-ρ)+ρ]   (Eq. 3.4)',
        val:  `θ₂ = ${result.maxStableSlopeDeg}°  — slopes above θ₂ experience full bed failure on saturation` },
      { label:'Equilibrium concentration C∞  (Eq. 2.25)',
        eq:   'C∞ = ρ·tanθ / [(σ-ρ)(tanφ - tanθ)]',
        val:  `C∞ = ${ρ}×${tanθ} / [(${σ}-${ρ})(${φ}-${tanθ})] = ${result.concentration}  (${result.concentrationThresholds?.pctOfCstar}% of C*)` },
      { label:'Mixture density ρT  (Eq. 2.37)',
        eq:   'ρT = σ·C∞ + (1-C∞)·ρ',
        val:  `ρT = ${σ}×${result.concentration} + ${(1-result.concentration).toFixed(4)}×${ρ} = ${result.mixDensity} kg/m³` },
      { label:'Mean velocity U  (Eq. 2.29 — dilatant fluid model)',
        eq:   'U = (2/5dₚ)·√(g·sinθ/ai·sinαi) · [(C+(1-C)ρ/σ)^½·(C*/C)^⅓-1]·h^(3/2)',
        val:  `U = ${result.velocity} m/s   (ai·sinαi = 0.02, Bagnold 1954)` },
      { label:'Dynamic impact pressure  (SABO design)',
        eq:   'P_dyn = ½·ρT·U²     P_sta = ρT·g·h·cosθ',
        val:  `P_dyn = ${result.dynamicPressure} kPa   P_sta = ${result.staticPressure} kPa   P_total = ${result.totalPressure} kPa` },
      { label:'Froude number',
        eq:   'Fr = U / √(g·h·cosθ)',
        val:  `Fr = ${result.velocity}/√(9.81×${h}×${cosθ}) = ${result.froudeNumber} → ${result.flowRegime}` },
      { label:'Discharge amplification  (Eq. 3.23)',
        eq:   'q_debris/q_water = C* / (C* - C∞)',
        val:  `${Cstar}/(${Cstar}-${result.concentration}) = ${result.dischargeAmplification}× — debris flow carries ${result.dischargeAmplification}× the water supply discharge` },
      { label:'Bagnold number Ba  (Eq. 2.127)',
        eq:   'Ba = σ·dₚ²·(du/dz) / [(C*/C)^⅓-1]^½ / μ',
        val:  `Ba ≈ ${result.bagnoldNumber}  ${result.bagnoldNumber > 450 ? '(> 450 → inertial/collision regime)' : result.bagnoldNumber < 40 ? '(< 40 → viscous regime)' : '(40–450 → transitional)'}` },
    ];
  })() : [];

  const flowTypeColors = {
    'stony': '#22d3ee', 'stony-immature': '#38bdf8', 'immature': '#818cf8',
    'viscous': '#f59e0b', 'turbulent-muddy': '#10b981', 'bedload': '#94a3b8'
  };
  const ftColor = result ? (flowTypeColors[result.flowTypeCode] || '#94a3b8') : null;

  return (
    <div className="animate-in">
      <div className="section-header">
        <div className="section-icon" style={{background:'rgba(14,165,233,0.12)'}}><Activity size={18} color="var(--accent-blue)"/></div>
        <div>
          <div className="section-title">Takahashi Debris Flow Model</div>
          <div className="section-desc">Physics-based analysis — Takahashi (2014) 2nd ed. Dilatant fluid model, Eqs. 2.25, 2.29, 3.4, 3.8</div>
        </div>
      </div>
      <div className="grid-2">
        <div className="panel">
          <div className="panel-header"><Cpu size={13} color="var(--accent-blue)"/><span className="panel-title">Input Parameters</span></div>
          <div className="panel-body">
            {Object.entries(params).map(([key,p])=>(
              <div className="form-group" key={key}>
                <label className="form-label"><Tip id={p.tipId}>{p.label} <span>[{p.unit||'—'}]</span></Tip></label>
                <input type="number" className="form-input" value={p.value} min={p.min} max={p.max} step={p.step} onChange={e=>setVal(key,e.target.value)}/>
              </div>
            ))}
            {error && <div className="alert alert-critical" style={{marginBottom:12}}><AlertTriangle size={14}/>{error}</div>}
            <LoadingBtn loading={loading} label="Run Analysis" loadingLabel="Computing…" onClick={run} icon={<ChevronRight size={13}/>}/>
          </div>
        </div>
        <div>
          {!result ? (
            <div className="panel" style={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center',minHeight:280}}>
              <div style={{textAlign:'center',color:'var(--text-muted)'}}>
                <Activity size={32} style={{margin:'0 auto 10px',opacity:0.25}}/>
                <div style={{fontSize:12,fontFamily:'var(--font-mono)'}}>Awaiting input parameters</div>
              </div>
            </div>
          ) : (
            <div className="animate-in" style={{display:'flex',flexDirection:'column',gap:12}}>
              {/* Hazard banner */}
              <div style={{background:`${rc}12`,border:`1px solid ${rc}30`,borderRadius:'var(--radius-lg)',padding:'14px 18px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div>
                    <div style={{fontSize:9,letterSpacing:'0.1em',textTransform:'uppercase',color:'var(--text-muted)',fontFamily:'var(--font-mono)',marginBottom:3}}>Hazard Classification</div>
                    <div style={{fontSize:20,fontWeight:700,color:rc,fontFamily:'var(--font-mono)'}}>{result.hazardLevel}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>Slope zone: <span style={{color:'var(--accent-cyan)'}}>{result.slopeZone?.toUpperCase()}</span></div>
                  </div>
                  <div style={{textAlign:'right',flexShrink:0}}>
                    <div style={{fontSize:11,fontWeight:700,color:ftColor,fontFamily:'var(--font-mono)'}}>{result.flowType}</div>
                    <div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>{result.flowRegime}</div>
                  </div>
                </div>
                {result.slopeZoneDesc && <div style={{marginTop:8,fontSize:10,color:'var(--text-muted)',fontFamily:'var(--font-mono)',lineHeight:1.6}}>{result.slopeZoneDesc}</div>}
              </div>

              {/* Primary metrics */}
              <div className="grid-2" style={{gap:10}}>
                <MetricCard label="Mean Velocity U"    value={result.velocity}       unit="m/s"   accentColor="var(--accent-cyan)"   tipId="velocity"/>
                <MetricCard label="Dynamic Pressure"   value={result.dynamicPressure} unit="kPa"   accentColor="var(--accent-orange)" tipId="impactForce"/>
                <MetricCard label="Equilibrium C∞"     value={result.concentration}   unit="vol"   accentColor="var(--accent-green)"  tipId="concentration"/>
                <MetricCard label="Mixture Density ρT" value={result.mixDensity}      unit="kg/m³" accentColor="var(--accent-violet)" tipId="mixDensity"/>
              </div>

              {/* Detailed outputs */}
              <div className="panel">
                <div className="panel-header"><TrendingUp size={12} color="var(--text-muted)"/><span className="panel-title">Full Diagnostics — Takahashi (2014)</span></div>
                <div className="panel-body" style={{padding:'10px 16px'}}>
                  {[
                    ['Unit Discharge',      `${result.discharge} m²/s`,                 'discharge'],
                    ['Total Pressure',      `${result.totalPressure} kPa (dyn+static)`,  null],
                    ['Static Pressure',     `${result.staticPressure} kPa`,              null],
                    ['Froude Number',       result.froudeNumber,                         'froudeNumber'],
                    ['Relative Depth h/dₚ', result.relativeDepth,                        null],
                    ['Bagnold Number',      result.bagnoldNumber + (result.bagnoldNumber>450?' ▶ inertial':result.bagnoldNumber<40?' ▶ viscous':' ▶ transitional'), null],
                    ['Discharge Amplif.',   `${result.dischargeAmplification}× water supply`, null],
                    ['Flow Type',           result.flowType,                             null],
                    ['θ₁ (min initiation)', `${result.minInitiationSlopeDeg}°`,          null],
                    ['θ₂ (max stable bed)', `${result.maxStableSlopeDeg}°`,              null],
                    ['C∞ as % of C*',       `${result.concentrationThresholds?.pctOfCstar}%`, null],
                  ].map(([k,v,t])=>(
                    <div className="stat-row" key={k}><span className="stat-key">{t?<Tip id={t}>{k}</Tip>:k}</span><span className="stat-val">{v}</span></div>
                  ))}
                  {result.flowTypeMechanism && (
                    <div style={{marginTop:10,padding:'8px 12px',background:'rgba(14,165,233,0.06)',border:'1px solid rgba(14,165,233,0.18)',borderRadius:'var(--radius)',fontSize:10,color:'var(--text-muted)',lineHeight:1.7,fontFamily:'var(--font-mono)'}}>
                      <strong style={{color:'var(--accent-cyan)'}}>Mechanism:</strong> {result.flowTypeMechanism}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {result && <CalcPanel title="STEP-BY-STEP CALCULATION — TAKAHASHI (2014)" color="#0ea5e9" steps={calcSteps}/>}
    </div>
  );
}

function AnalyzeMonteCarlo() {
  const [params, setParams] = useState({slopeAngle:22,grainDiameter:50,waterDepth:2,particleDensity:2650,fluidDensity:1100,iterations:500});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const run = async () => {
    setLoading(true); setError(null);
    try {
      const data = await (await fetch(`${API}/monte-carlo`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(params)})).json();
      if(data.error) throw new Error(data.error);
      setResult(data);
    } catch(e){setError(e.message);}
    setLoading(false);
  };
  const buildHist = (data,bins=20) => {
    if(!data?.length) return null;
    const vals=data.map(d=>d.velocity), mn=Math.min(...vals), mx=Math.max(...vals), bs=(mx-mn)/bins;
    const counts=new Array(bins).fill(0);
    vals.forEach(v=>{const i=Math.min(bins-1,Math.floor((v-mn)/bs));counts[i]++;});
    return {labels:counts.map((_,i)=>(mn+i*bs).toFixed(1)),datasets:[{data:counts,backgroundColor:counts.map((_,i)=>{const n=i/bins;return n>0.75?'rgba(239,68,68,0.7)':n>0.5?'rgba(249,115,22,0.7)':'rgba(14,165,233,0.7)';}),borderColor:'transparent',borderRadius:2}]};
  };
  const fields=[['slopeAngle','Slope Angle','°','slopeAngle'],['grainDiameter','Grain Diameter','mm','grainDiameter'],['waterDepth','Flow Depth','m','waterDepth'],['particleDensity','Particle Density','kg/m³','particleDensity'],['fluidDensity','Fluid Density','kg/m³','fluidDensity'],['iterations','Iterations (100–2000)','','iterations']];
  return (
    <div className="animate-in">
      <div className="section-header">
        <div className="section-icon" style={{background:'rgba(168,85,247,0.12)'}}><BarChart2 size={18} color="var(--accent-purple)"/></div>
        <div><div className="section-title">Monte Carlo Simulation</div><div className="section-desc">N iterations with Gaussian-perturbed inputs (Box-Muller, σ=8–15%)</div></div>
      </div>
      <div className="grid-2">
        <div className="panel">
          <div className="panel-header"><Cpu size={13} color="var(--accent-purple)"/><span className="panel-title">Simulation Parameters</span></div>
          <div className="panel-body">
            {fields.map(([key,label,unit,tipId])=>(
              <div className="form-group" key={key}>
                <label className="form-label"><Tip id={tipId}>{label}{unit&&<span> [{unit}]</span>}</Tip></label>
                <input type="number" className="form-input" value={params[key]} onChange={e=>setParams(p=>({...p,[key]:parseFloat(e.target.value)}))}/>
              </div>
            ))}
            {error && <div className="alert alert-critical" style={{marginBottom:12}}><AlertTriangle size={14}/>{error}</div>}
            <LoadingBtn loading={loading} label="Run Monte Carlo" loadingLabel={`Simulating ${params.iterations}…`} onClick={run} icon={<Zap size={13}/>}/>
          </div>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {result ? (
            <>
              <div className="panel animate-in">
                <div className="panel-header">
                  <TrendingUp size={12} color="var(--accent-purple)"/>
                  <span className="panel-title">Velocity Distribution — {result.iterations} runs</span>
                  <button className="btn btn-secondary" style={{marginLeft:'auto',padding:'3px 9px',fontSize:10}} onClick={()=>exportCSV('mc.csv',['ID','Velocity','Conc','Impact','Hazard'],result.distributions.map(d=>[d.id,d.velocity,d.concentration,d.impactForce,d.hazardLevel]))}>
                    <Download size={10}/> CSV
                  </button>
                </div>
                <div className="panel-body">
                  <div className="chart-container" style={{height:180}}>
                    {buildHist(result.distributions)&&<Bar data={buildHist(result.distributions)} options={{...CHART_OPTS,scales:{...CHART_OPTS.scales,x:{...CHART_OPTS.scales.x,title:{display:true,text:'Velocity (m/s)',color:'#475569',font:{size:9}}}}}}/>}
                  </div>
                </div>
              </div>
              <div className="panel animate-in">
                <div className="panel-header"><Info size={12} color="var(--text-muted)"/><span className="panel-title">Statistical Summary</span></div>
                <div className="panel-body" style={{padding:'10px 16px'}}>
                  {['velocity','impactForce','concentration'].map(key=>{
                    const s=result.summary[key];
                    const L={velocity:'Velocity (m/s)',impactForce:'Impact (kPa)',concentration:'Concentration'};
                    return (
                      <div key={key} style={{marginBottom:10}}>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
                          <span style={{fontSize:9,textTransform:'uppercase',color:'var(--text-muted)',fontFamily:'var(--font-mono)',letterSpacing:'0.06em'}}>{L[key]}</span>
                          <span style={{fontSize:10,color:'var(--text-secondary)',fontFamily:'var(--font-mono)'}}>μ={s.mean} σ={s.std}</span>
                        </div>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:3}}>
                          {[['P5',s.p5],['P50',s.p50],['Mean',s.mean],['P95',s.p95],['Max',s.max]].map(([l,v])=>(
                            <div key={l} style={{background:'var(--bg-elevated)',borderRadius:3,padding:'3px 5px',textAlign:'center'}}>
                              <div style={{fontSize:8,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>{l}</div>
                              <div style={{fontSize:10,fontFamily:'var(--font-mono)',fontWeight:700,color:'var(--text-secondary)'}}>{v}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  <div className="sep" style={{margin:'8px 0'}}/>
                  {Object.entries(result.hazardDistribution).map(([level,count])=>(
                    <div key={level} style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:3}}>
                      <RiskBadge level={level}/>
                      <span style={{fontFamily:'var(--font-mono)',fontSize:11,color:'var(--text-secondary)'}}>{count} ({((count/result.iterations)*100).toFixed(1)}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="panel" style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',minHeight:200}}>
              <div style={{textAlign:'center',color:'var(--text-muted)'}}>
                <BarChart2 size={32} style={{margin:'0 auto 10px',opacity:0.2}}/>
                <div style={{fontSize:12,fontFamily:'var(--font-mono)'}}>Configure and run simulation</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AnalyzeDEM() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [filename, setFilename] = useState(null);
  const fileRef = useRef();
  const canvasRef = useRef();
  const extra = useRef({grainDiameter:50,waterDepth:2});
  const processFile = async file => {
    if(!file) return;
    setFilename(file.name); setLoading(true); setError(null); setResult(null);
    const fd=new FormData(); fd.append('dem',file); fd.append('grainDiameter',extra.current.grainDiameter); fd.append('waterDepth',extra.current.waterDepth);
    try {
      const data=await (await fetch(`${API}/analyze-dem`,{method:'POST',body:fd})).json();
      if(data.error) throw new Error(data.error);
      setResult(data);
    } catch(e){setError(e.message);}
    setLoading(false);
  };
  useEffect(()=>{
    if(!result||!canvasRef.current) return;
    const canvas=canvasRef.current, ctx=canvas.getContext('2d');
    const {spatialSample,gridWidth,gridHeight}=result;
    if(!spatialSample?.length) return;
    canvas.width=Math.min(gridWidth,280); canvas.height=Math.min(gridHeight,190);
    const sx=canvas.width/gridWidth, sy=canvas.height/gridHeight;
    ctx.clearRect(0,0,canvas.width,canvas.height);
    spatialSample.forEach(p=>{ctx.fillStyle=p.color||'#1e3a5f';ctx.fillRect(Math.round(p.x*sx),Math.round(p.y*sy),Math.max(1,Math.ceil(sx)),Math.max(1,Math.ceil(sy)));});
  },[result]);
  const classInfo=[{class:'Bedrock/Cliff',color:'#7c3aed',threshold:'>45°'},{class:'Initiation',color:'#ef4444',threshold:'30–45°'},{class:'Transport',color:'#f59e0b',threshold:'15–30°'},{class:'Deposition',color:'#10b981',threshold:'2–15°'},{class:'Stable',color:'#94a3b8',threshold:'<2°'}];
  return (
    <div className="animate-in">
      <div className="section-header">
        <div className="section-icon" style={{background:'rgba(16,185,129,0.12)'}}><Mountain size={18} color="var(--accent-green)"/></div>
        <div><div className="section-title">DEM Geoprocessing Engine</div><div className="section-desc">GeoTIFF slope extraction via <Tip id="hornSlope">Horn's algorithm</Tip>, zone classification, Monte Carlo velocity</div></div>
      </div>
      <div className="grid-2" style={{marginBottom:18}}>
        <div>
          <div className="panel" style={{marginBottom:12}}>
            <div className="panel-header"><Cpu size={12} color="var(--accent-green)"/><span className="panel-title">Flow Parameters</span></div>
            <div className="panel-body">
              <div className="form-group"><label className="form-label"><Tip id="grainDiameter">Grain Diameter <span>[mm]</span></Tip></label><input type="number" className="form-input" defaultValue={50} onChange={e=>{extra.current.grainDiameter=parseFloat(e.target.value);}}/></div>
              <div className="form-group"><label className="form-label"><Tip id="waterDepth">Flow Depth <span>[m]</span></Tip></label><input type="number" className="form-input" defaultValue={2} onChange={e=>{extra.current.waterDepth=parseFloat(e.target.value);}}/></div>
            </div>
          </div>
          <div className={`file-drop${dragOver?' drag-over':''}`} onDragOver={e=>{e.preventDefault();setDragOver(true);}} onDragLeave={()=>setDragOver(false)} onDrop={e=>{e.preventDefault();setDragOver(false);processFile(e.dataTransfer.files[0]);}} onClick={()=>fileRef.current?.click()}>
            <input type="file" ref={fileRef} style={{display:'none'}} accept=".tif,.tiff" onChange={e=>processFile(e.target.files[0])}/>
            {loading ? (<div style={{color:'var(--text-muted)'}}><span className="spinner" style={{width:24,height:24,margin:'0 auto 10px',display:'block'}}/><div style={{fontFamily:'var(--font-mono)',fontSize:12}}>Processing terrain…</div></div>) : (<><Upload size={28} style={{margin:'0 auto 10px',color:'var(--text-muted)',opacity:0.4,display:'block'}}/><div style={{fontFamily:'var(--font-mono)',fontSize:12,color:'var(--text-secondary)',marginBottom:3}}>{filename?`✓ ${filename}`:'Drop GeoTIFF DEM or click to browse'}</div><div style={{fontSize:11,color:'var(--text-muted)'}}>Accepts .tif/.tiff — UTM projected recommended</div></>)}
          </div>
          {error && <div className="alert alert-critical" style={{marginTop:10}}><AlertTriangle size={13}/>{error}</div>}
        </div>
        <div className="panel">
          <div className="panel-header"><Layers size={12} color="var(--accent-green)"/><span className="panel-title">Zone Classification</span></div>
          <div className="panel-body">
            {classInfo.map(c=>(
              <div key={c.class} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 0',borderBottom:'1px solid var(--border)'}}>
                <div style={{width:10,height:10,borderRadius:2,background:c.color,flexShrink:0}}/>
                <div style={{flex:1,fontFamily:'var(--font-mono)',fontSize:11,fontWeight:700,color:c.color}}>{c.class}</div>
                <div style={{fontFamily:'var(--font-mono)',fontSize:10,color:'var(--text-muted)'}}>{c.threshold}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {result && (
        <div className="animate-in" style={{display:'flex',flexDirection:'column',gap:14}}>
          <div className="grid-4">
            <MetricCard label="Mean Slope"   value={`${result.slopeStats.mean}°`} accentColor="var(--accent-green)"/>
            <MetricCard label="Max Slope"    value={`${result.slopeStats.max}°`}  accentColor="var(--accent-orange)"/>
            <MetricCard label="MC P95"       value={`${result.monteCarloVelocity.p95} m/s`} accentColor="var(--accent-red)"/>
            <MetricCard label="Valid Pixels" value={result.rasterInfo.validPixels?.toLocaleString()} accentColor="var(--accent-cyan)"/>
          </div>
          <div className="grid-2">
            <div className="panel"><div className="panel-header"><Mountain size={12} color="var(--accent-green)"/><span className="panel-title">Slope Map</span></div><div className="panel-body" style={{display:'flex',justifyContent:'center'}}><canvas ref={canvasRef} style={{maxWidth:'100%',imageRendering:'pixelated',borderRadius:4}}/></div></div>
            <div className="panel">
              <div className="panel-header"><Layers size={12} color="var(--text-muted)"/><span className="panel-title">Zone Statistics</span></div>
              <div className="panel-body" style={{padding:'10px 14px'}}>
                {Object.entries(result.classStats||{}).map(([cls,count])=>{
                  const info=classInfo.find(c=>c.class===cls);
                  const total=Object.values(result.classStats).reduce((a,b)=>a+b,0);
                  const pct=total>0?((count/total)*100).toFixed(1):0;
                  return (<div key={cls} style={{marginBottom:9}}><div style={{display:'flex',justifyContent:'space-between',marginBottom:2}}><span style={{fontSize:10,fontFamily:'var(--font-mono)',color:info?.color||'#94a3b8'}}>{cls}</span><span style={{fontSize:10,fontFamily:'var(--font-mono)',color:'var(--text-muted)'}}>{count.toLocaleString()} ({pct}%)</span></div><div className="pct-bar-track"><div className="pct-bar-fill" style={{width:`${pct}%`,background:info?.color||'var(--accent-blue)'}}/></div></div>);
                })}
                <div className="sep"/>
                {[['P5',result.monteCarloVelocity.p5],['P50',result.monteCarloVelocity.p50],['P95',result.monteCarloVelocity.p95]].map(([l,v])=>(
                  <div className="stat-row" key={l}><span className="stat-key">{l}</span><span className="stat-val" style={{color:'var(--accent-cyan)'}}>{v} m/s</span></div>
                ))}
              </div>
            </div>
          </div>
          {result.slopeHistogram && (
            <div className="panel">
              <div className="panel-header"><BarChart2 size={12} color="var(--text-muted)"/><span className="panel-title">Slope Frequency Distribution</span></div>
              <div className="panel-body">
                <div className="chart-container" style={{height:160}}>
                  <Bar data={{labels:result.slopeHistogram.map(b=>b.label),datasets:[{data:result.slopeHistogram.map(b=>b.count),backgroundColor:result.slopeHistogram.map(b=>{const d=parseInt(b.label);return d>30?'rgba(239,68,68,0.7)':d>15?'rgba(245,158,11,0.7)':d>2?'rgba(16,185,129,0.7)':'rgba(148,163,184,0.4)';}),borderColor:'transparent',borderRadius:2}]}} options={CHART_OPTS}/>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AnalyzeRainfall() {
  const [params, setParams] = useState({duration:3,intensity:18,alpha:14.82,beta:-0.39,postFire:false,region:'Global'});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try { setResult(await (await fetch(`${API}/analyze-rainfall`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(params)})).json()); }
    catch(e){console.error(e);}
    setLoading(false);
  };
  const buildIDChart = (tc,ud,ui) => {
    if(!tc) return null;
    const exceeded=ui>=(tc.find(p=>p.duration===ud)?.threshold||0);
    const idx=tc.findIndex(p=>p.duration===ud);
    return {labels:tc.map(p=>`${p.duration}h`),datasets:[
      {label:'I-D Threshold (Caine 1980)',data:tc.map(p=>p.threshold),borderColor:'rgba(14,165,233,0.8)',backgroundColor:'rgba(14,165,233,0.06)',borderWidth:2,tension:0.4,fill:true,
        pointRadius:tc.map((_,i)=>i===idx?10:3),pointStyle:tc.map((_,i)=>i===idx?'triangle':'circle'),
        pointBackgroundColor:tc.map((_,i)=>i===idx?(exceeded?'rgba(239,68,68,0.95)':'rgba(34,197,94,0.95)'):'rgba(14,165,233,0.7)'),
        pointBorderColor:tc.map((_,i)=>i===idx?'#fff':'transparent'),pointBorderWidth:tc.map((_,i)=>i===idx?1.5:0)},
      {label:'Observed Event',data:tc.map((_,i)=>i===idx?ui:null),borderColor:'transparent',backgroundColor:exceeded?'rgba(239,68,68,0.9)':'rgba(34,197,94,0.9)',pointRadius:tc.map((_,i)=>i===idx?11:0),pointStyle:'triangle',showLine:false}
    ]};
  };
  const aC={'CRITICAL':'alert-critical','WARNING':'alert-warning','WATCH':'alert-watch','ADVISORY':'alert-info','STABLE':'alert-stable'};
  const idSteps = result ? [
    {label:'Post-Fire Correction',eq:'α_eff = α × 0.6  (if enabled)',val:result.postFire?`${params.alpha}×0.6 = ${(params.alpha*0.6).toFixed(2)}`:`No correction — α_eff = ${params.alpha}`},
    {label:'Critical Intensity I_c',eq:'I_c = α_eff · D^β',val:`${result.postFire?(params.alpha*0.6).toFixed(2):params.alpha}×${params.duration}^(${params.beta}) = ${result.criticalIntensity} mm/h`},
    {label:'Exceedance Ratio',eq:'ER = I_obs / I_c',val:`${result.intensity}/${result.criticalIntensity} = ${result.exceedanceRatio}×`},
    {label:'Safety Factor',eq:'SF = 1 / ER',val:`1/${result.exceedanceRatio} = ${result.safetyFactor}`},
    {label:'Risk Classification',eq:'ER>1.5→CRITICAL  |  >1.2→WARNING  |  >1.0→WATCH',val:`→ ${result.riskLevel}`}
  ] : [];
  return (
    <div className="animate-in">
      <div className="section-header">
        <div className="section-icon" style={{background:'rgba(245,158,11,0.12)'}}><CloudRain size={18} color="var(--accent-orange)"/></div>
        <div><div className="section-title">Precipitation Trigger Assessment — Global</div><div className="section-desc">Global I-D threshold I = <Tip id="alpha">α</Tip>·D^<Tip id="beta">β</Tip> (Caine 1980). For Canadian sites, use the <strong style={{color:'#fca5a5'}}>Canadian IDF → ⚠️ Trigger Assessment</strong> tab for ECCC station-calibrated thresholds.</div></div>
      </div>
      <div className="grid-2">
        <div className="panel">
          <div className="panel-header"><CloudRain size={12} color="var(--accent-orange)"/><span className="panel-title">Event Parameters</span></div>
          <div className="panel-body">
            {[['Duration','duration','h','duration',1],['Rainfall Intensity','intensity','mm/h','intensity',0.1],['α Coefficient','alpha','','alpha',0.1],['β Exponent','beta','','beta',0.01]].map(([label,key,unit,tipId,step])=>(
              <div className="form-group" key={key}><label className="form-label"><Tip id={tipId}>{label}{unit&&<span> [{unit}]</span>}</Tip></label><input type="number" className="form-input" value={params[key]} step={step} onChange={e=>setParams(p=>({...p,[key]:parseFloat(e.target.value)}))}/></div>
            ))}
            <div className="form-group"><label className="form-label">Region</label><select className="form-input" value={params.region} onChange={e=>setParams(p=>({...p,region:e.target.value}))}>
              {['Global','European Alps','Rocky Mountains','Himalayas','Taiwan','New Zealand'].map(r=><option key={r}>{r}</option>)}
            </select></div>
            <div className="toggle-row">
              <div><div style={{fontSize:12,color:'var(--text-secondary)',fontWeight:600}}>Post-Fire Correction</div><div style={{fontSize:11,color:'var(--text-muted)'}}>40% reduction to α</div></div>
              <label className="toggle"><input type="checkbox" checked={params.postFire} onChange={e=>setParams(p=>({...p,postFire:e.target.checked}))}/><span className="toggle-slider"/></label>
            </div>
            <div style={{marginTop:14}}><LoadingBtn loading={loading} label="Assess Trigger Potential" loadingLabel="Analyzing…" onClick={run} icon={<CloudRain size={13}/>}/></div>
          </div>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {result ? (
            <>
              <div className={`alert ${aC[result.riskLevel]||'alert-stable'} animate-in`}>
                {result.isTriggered?<AlertTriangle size={20}/>:<CheckCircle size={20}/>}
                <div><div style={{fontWeight:700,fontSize:14,marginBottom:2}}>SYSTEM {result.riskLevel}{result.postFire&&<span style={{marginLeft:8,fontSize:10,opacity:0.8}}>🔥 POST-FIRE</span>}</div><div style={{fontSize:11,opacity:0.85}}>{result.intensity} mm/h — Threshold: {result.criticalIntensity} mm/h · ER: <strong>{result.exceedanceRatio}×</strong></div></div>
              </div>
              <div className="grid-3" style={{gap:9}}>
                <MetricCard label="Exceedance"  value={result.exceedanceRatio}   accentColor={result.isTriggered?'#ef4444':'var(--accent-green)'} tipId="exceedance"/>
                <MetricCard label="Safety Factor" value={result.safetyFactor}    accentColor="var(--accent-cyan)"    tipId="safetyFactor"/>
                <MetricCard label="Critical I"  value={result.criticalIntensity} unit="mm/h" accentColor="var(--accent-orange)" tipId="intensity"/>
              </div>
              <div className="panel animate-in">
                <div className="panel-header"><TrendingUp size={12} color="var(--accent-orange)"/><span className="panel-title">I-D Threshold Curve</span></div>
                <div className="panel-body"><div className="chart-container" style={{height:200}}>
                  {result.thresholdCurve&&<Line data={buildIDChart(result.thresholdCurve,result.duration,result.intensity)} options={{...CHART_OPTS,plugins:{legend:{display:true,labels:{color:'#94a3b8',font:{size:9,family:'Space Mono'}}},tooltip:{callbacks:{label:c=>`${c.dataset.label}: ${c.raw} mm/h`}}},scales:{x:{...CHART_OPTS.scales.x,title:{display:true,text:'Duration',color:'#475569',font:{size:9}}},y:{...CHART_OPTS.scales.y,title:{display:true,text:'Intensity (mm/h)',color:'#475569',font:{size:9}}}}}}/>}
                </div></div>
              </div>
            </>
          ) : (
            <div className="panel" style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',minHeight:200}}>
              <div style={{textAlign:'center',color:'var(--text-muted)'}}><CloudRain size={32} style={{margin:'0 auto 10px',opacity:0.2}}/><div style={{fontSize:12,fontFamily:'var(--font-mono)'}}>Enter event data and run analysis</div></div>
            </div>
          )}
        </div>
      </div>
      {result && <CalcPanel title="I-D CALCULATION STEPS" color="#f59e0b" steps={idSteps}/>}
    </div>
  );
}

function AnalyzeWeather() {
  const [coords, setCoords] = useState({lat:46.5,lon:8.0});
  const [apiKey, setApiKey] = useState('');
  const [weather, setWeather] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const intervalRef = useRef(null);
  const fetch_ = useCallback(async()=>{
    setLoading(true);
    try {
      const w=await (await fetch(`${API}/live-weather?lat=${coords.lat}&lon=${coords.lon}&apiKey=${apiKey}`)).json();
      setWeather(w);
      const a=await (await fetch(`${API}/analyze-rainfall`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({intensity:w.intensity,duration:1})})).json();
      setAnalysis(a);
    }catch(e){console.error(e);}
    setLoading(false);
  },[coords,apiKey]);
  const togglePolling=()=>{if(polling){clearInterval(intervalRef.current);setPolling(false);}else{fetch_();intervalRef.current=setInterval(fetch_,10*60*1000);setPolling(true);}};
  useEffect(()=>()=>clearInterval(intervalRef.current),[]);
  const rc=analysis?(RISK_COLORS[analysis.riskLevel]||'#94a3b8'):null;
  return (
    <div className="animate-in">
      <div className="section-header">
        <div className="section-icon" style={{background:'rgba(56,189,248,0.12)'}}><Radio size={18} color="var(--text-accent)"/></div>
        <div><div className="section-title">Live Terrain Monitor</div><div className="section-desc">Real-time precipitation via OpenWeatherMap, auto-triggered against Caine (1980) I-D threshold</div></div>
      </div>
      <div className="grid-2">
        <div className="panel">
          <div className="panel-header"><Radio size={12} color="var(--text-accent)"/><span className="panel-title">Site Configuration</span></div>
          <div className="panel-body">
            <div className="grid-2" style={{gap:10,marginBottom:12}}>
              <div><label className="form-label">Latitude <span>[°N]</span></label><input type="number" className="form-input" value={coords.lat} step={0.01} onChange={e=>setCoords(c=>({...c,lat:parseFloat(e.target.value)}))} /></div>
              <div><label className="form-label">Longitude <span>[°E]</span></label><input type="number" className="form-input" value={coords.lon} step={0.01} onChange={e=>setCoords(c=>({...c,lon:parseFloat(e.target.value)}))} /></div>
            </div>
            <div className="form-group"><label className="form-label">API Key <span>[optional — leave blank for demo]</span></label><input type="text" className="form-input" placeholder="Leave blank for demo mode" value={apiKey} onChange={e=>setApiKey(e.target.value)}/></div>
            <div className="alert alert-info" style={{marginBottom:12,fontSize:11}}><Info size={12}/><span>Demo mode uses simulated rainfall. Free API key at openweathermap.org.</span></div>
            <div className="grid-2" style={{gap:9}}>
              <button className={`btn ${polling?'btn-danger':'btn-success'} btn-full`} onClick={togglePolling}>{polling?<><span className="spinner"/>Stop</>:<><Radio size={13}/>Start Monitor</>}</button>
              <button className="btn btn-secondary btn-full" onClick={fetch_} disabled={loading}><RefreshCw size={13}/>Fetch</button>
            </div>
          </div>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {weather ? (
            <>
              <div className="live-monitor animate-in">
                <div className="live-header">
                  <div><div style={{fontFamily:'var(--font-mono)',fontSize:12,fontWeight:700,color:'var(--text-primary)'}}>📍 {weather.location}</div><div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>{weather.mock?'⚠ Demo mode — simulated data':`Updated: ${new Date(weather.timestamp*1000).toLocaleTimeString()}`}</div></div>
                  {polling&&<div className="live-pulse"><span className="status-dot"/>LIVE</div>}
                </div>
                <div className="grid-2" style={{gap:9,marginBottom:10}}>
                  <MetricCard label="Rain (1h)" value={`${weather.intensity}`} unit="mm/h" accentColor="var(--accent-cyan)"/>
                  <MetricCard label="Temp" value={`${weather.temperature}°`} unit="Celsius" accentColor="var(--accent-blue)"/>
                  {weather.humidity!==undefined&&<MetricCard label="Humidity" value={`${weather.humidity}%`} accentColor="var(--text-muted)"/>}
                  {weather.windSpeed!==undefined&&<MetricCard label="Wind" value={`${weather.windSpeed}`} unit="m/s" accentColor="var(--accent-violet)"/>}
                </div>
                {analysis&&<div className={`alert ${analysis.riskLevel==='CRITICAL'?'alert-critical':analysis.riskLevel==='WARNING'?'alert-warning':analysis.riskLevel==='WATCH'?'alert-watch':'alert-stable'}`}>
                  {analysis.isTriggered?<AlertTriangle size={16}/>:<CheckCircle size={16}/>}
                  <div><div style={{fontWeight:700,fontSize:12}}>Hazard: {analysis.riskLevel}</div><div style={{fontSize:11}}>{weather.intensity} mm/h vs {analysis.criticalIntensity} mm/h · ER:{analysis.exceedanceRatio}×</div></div>
                </div>}
              </div>
              {analysis&&<div className="panel animate-in">
                <div className="panel-header"><TrendingUp size={12} color="var(--text-muted)"/><span className="panel-title">I-D Assessment (1h)</span></div>
                <div className="panel-body" style={{padding:'10px 14px'}}>
                  {[['Observed',`${weather.intensity} mm/h`,null],['Critical (Caine 1980)',`${analysis.criticalIntensity} mm/h`,null],['Exceedance Ratio',`${analysis.exceedanceRatio}×`,'exceedance'],['Safety Factor',analysis.safetyFactor,'safetyFactor'],['Debris Flow Risk',analysis.isTriggered?'⚠ EXCEEDED':'✓ Below threshold',null]].map(([k,v,t])=>(
                    <div className="stat-row" key={k}><span className="stat-key">{t?<Tip id={t}>{k}</Tip>:k}</span><span className="stat-val" style={{color:k==='Debris Flow Risk'?rc:undefined}}>{v}</span></div>
                  ))}
                </div>
              </div>}
            </>
          ) : (
            <div className="panel" style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',minHeight:200}}>
              <div style={{textAlign:'center',color:'var(--text-muted)'}}><Radio size={32} style={{margin:'0 auto 10px',opacity:0.2}}/><div style={{fontSize:12,fontFamily:'var(--font-mono)'}}>Enter coordinates and start the monitor</div></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MODULE 6: CANADIAN IDF + ECCC WEATHER
// ═══════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════
// MODULE 6: CANADIAN IDF — CHILD TABS
// Tabs: Stations | IDF Curves | Live Analysis | Learn
// ═══════════════════════════════════════════════════════════

const PROVINCES = ['All','AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'];

// ── Canadian regional debris flow thresholds ────────────────
const CANADA_THRESHOLDS = [
  { region:'Global baseline',              province:'—',  alpha:14.82, beta:-0.39, source:'Caine (1980)' },
  { region:'Coastal BC (Vancouver/Fraser)',province:'BC', alpha:22.0,  beta:-0.50, source:'BC Ministry of Forests; ECCC Haney stn' },
  { region:'Interior BC / AB Rockies',     province:'BC/AB', alpha:18.5, beta:-0.48, source:'Regional geotechnical studies' },
  { region:'AB Prairies (Calgary/Edmonton)',province:'AB', alpha:16.0, beta:-0.42, source:'ECCC derived — Gumbel fit' },
  { region:'SK / MB Prairies',             province:'SK/MB', alpha:15.5, beta:-0.41, source:'ECCC Engineering Climate Datasets' },
  { region:'ON Great Lakes / Southern',    province:'ON', alpha:19.5, beta:-0.51, source:'ECCC Toronto/Hamilton stations' },
  { region:'ON Northern (Thunder Bay)',    province:'ON', alpha:16.8, beta:-0.46, source:'ECCC northern Ontario stations' },
  { region:'QC St. Lawrence Valley',       province:'QC', alpha:18.8, beta:-0.49, source:'Mailhot et al. (2007)' },
  { region:'QC Laurentians',               province:'QC', alpha:17.5, beta:-0.47, source:'Regional climate models' },
  { region:'Maritimes (NB/NS/PE)',         province:'NB/NS/PE', alpha:20.0, beta:-0.52, source:'Atlantic Canada geohazard studies' },
  { region:'Newfoundland (St. John\'s)',   province:'NL', alpha:21.5, beta:-0.54, source:'Highest in Canada; extratropical cyclones' },
  { region:'Labrador',                     province:'NL', alpha:14.5, beta:-0.42, source:'Subarctic continental influence' },
  { region:'Yukon (Whitehorse)',           province:'YT', alpha: 6.8, beta:-0.38, source:'Subarctic; low intensity events' },
  { region:'NT / NU (Yellowknife)',        province:'NT/NU', alpha: 6.0, beta:-0.36, source:'Arctic; permafrost critical factor' },
  { region:'Post-fire BC/AB (empirical)', province:'BC/AB', alpha: 8.89, beta:-0.39, source:'Caine×0.6; Staley et al. (2013)' },
  { region:'Post-fire prairies',           province:'AB/SK', alpha: 7.5, beta:-0.38, source:'Empirical 40% threshold reduction' },
  { region:'Italy (all)',                  province:'—',  alpha:19.0, beta:-0.50, source:'Brunetti et al. (2010)' },
  { region:'European Alps',               province:'—',  alpha:17.8, beta:-0.45, source:'Guzzetti et al. (2008)' },
];

const RETURN_COLORS = {
  '2-yr': '#22d3ee', '5-yr': '#38bdf8', '10-yr': '#818cf8',
  '25-yr': '#f59e0b', '50-yr': '#f97316', '100-yr': '#ef4444'
};

// ─── Sub-component: Station Finder ──────────────────────────
function CanadaStationsTab({ selected, setSelected }) {
  const [coords, setCoords]   = useState({ lat: 51.05, lon: -114.07 });
  const [province, setProv]   = useState('All');
  const [stations, setStns]   = useState([]);
  const [nearest, setNearest] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [loaded, setLoaded]   = useState(false);

  useEffect(() => {
    fetch(`${API}/canadian-idf-stations`)
      .then(r => r.json())
      .then(d => { setStns(d.stations || []); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const findNearest = async () => {
    setLoading(true); setNearest([]);
    try {
      const d = await (await fetch(`${API}/canadian-idf-nearest?lat=${coords.lat}&lon=${coords.lon}&n=8`)).json();
      setNearest(d.nearest || []);
      if (d.nearest?.length > 0 && !selected) setSelected(d.nearest[0]);
    } catch(e){}
    setLoading(false);
  };

  const filtered = stations.filter(s => {
    const mp = province === 'All' || s.province === province;
    const ms = !search || s.city.toLowerCase().includes(search.toLowerCase()) || s.name.toLowerCase().includes(search.toLowerCase());
    return mp && ms;
  });

  const StationRow = ({ s, distance }) => (
    <button onClick={() => setSelected(s)} style={{
      width:'100%', background: selected?.id===s.id ? 'rgba(239,68,68,0.1)' : 'transparent',
      border:`1px solid ${selected?.id===s.id ? 'rgba(239,68,68,0.3)' : 'transparent'}`,
      borderRadius:'var(--radius-sm)', padding:'8px 10px', marginBottom:3,
      cursor:'pointer', textAlign:'left', transition:'all 0.12s'
    }}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <span style={{fontSize:11,fontWeight:700,color:selected?.id===s.id?'#fca5a5':'var(--text-secondary)',fontFamily:'var(--font-mono)'}}>{s.city}</span>
          <span style={{marginLeft:6,fontSize:9,color:'var(--text-muted)',fontFamily:'var(--font-mono)',background:'rgba(255,255,255,0.06)',padding:'1px 5px',borderRadius:3}}>{s.province}</span>
          {distance && <span style={{marginLeft:6,fontSize:9,color:'var(--accent-cyan)',fontFamily:'var(--font-mono)'}}>{distance} km</span>}
        </div>
        <span style={{fontSize:9,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>α={s.alpha} β={s.beta}</span>
      </div>
      <div style={{fontSize:10,color:'var(--text-muted)',marginTop:1}}>{s.name}</div>
    </button>
  );

  return (
    <div className="grid-2" style={{gap:16}}>
      <div className="panel">
        <div className="panel-header"><MapPin size={13} color="#ef4444"/><span className="panel-title">Find Nearest Station</span></div>
        <div className="panel-body">
          <div className="grid-2" style={{gap:10,marginBottom:12}}>
            <div><label className="form-label">Latitude [°N]</label><input type="number" className="form-input" value={coords.lat} step={0.01} onChange={e=>setCoords(c=>({...c,lat:parseFloat(e.target.value)}))}/></div>
            <div><label className="form-label">Longitude [°W/E]</label><input type="number" className="form-input" value={coords.lon} step={0.01} onChange={e=>setCoords(c=>({...c,lon:parseFloat(e.target.value)}))}/></div>
          </div>
          <LoadingBtn loading={loading} label="Find Nearest Stations" loadingLabel="Searching…" onClick={findNearest} icon={<Search size={13}/>}/>
          {nearest.length > 0 && (
            <div style={{marginTop:14}}>
              <div style={{fontSize:9,fontFamily:'var(--font-mono)',color:'var(--text-muted)',letterSpacing:'0.08em',marginBottom:6,textTransform:'uppercase'}}>Nearest ECCC IDF Stations</div>
              {nearest.map(s => <StationRow key={s.id} s={s} distance={s.distanceKm}/>)}
            </div>
          )}
        </div>
      </div>
      <div className="panel">
        <div className="panel-header"><Globe size={13} color="#ef4444"/><span className="panel-title">Browse All Stations ({loaded ? stations.length : '…'})</span></div>
        <div className="panel-body">
          <div style={{display:'flex',gap:8,marginBottom:10}}>
            <select className="form-input" style={{width:72,flexShrink:0}} value={province} onChange={e=>setProv(e.target.value)}>
              {PROVINCES.map(p=><option key={p}>{p}</option>)}
            </select>
            <input type="text" className="form-input" placeholder="Search city or station…" value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <div style={{maxHeight:380,overflowY:'auto'}}>
            {filtered.map(s=><StationRow key={s.id} s={s}/>)}
            {filtered.length===0 && loaded && <div style={{textAlign:'center',color:'var(--text-muted)',padding:'20px 0',fontSize:12}}>No stations match filter</div>}
          </div>
        </div>
      </div>
      {selected && (
        <div className="panel animate-in" style={{gridColumn:'1/-1'}}>
          <div className="panel-header">
            <span style={{fontSize:14}}>🍁</span>
            <span className="panel-title">Selected: {selected.city}, {selected.province} — {selected.name}</span>
            <span style={{marginLeft:'auto',fontSize:9,fontFamily:'var(--font-mono)',color:'var(--text-muted)'}}>ECCC Stn #{selected.id}</span>
          </div>
          <div className="panel-body">
            {selected.notes && <div className="alert alert-info" style={{marginBottom:12,fontSize:11}}><Info size={12}/>{selected.notes}</div>}
            <div className="grid-4" style={{gap:10}}>
              <MetricCard label="α (2-yr baseline)" value={selected.alpha} accentColor="#ef4444" tipId="alpha"/>
              <MetricCard label="β (exponent)"       value={selected.beta}  accentColor="#f59e0b" tipId="beta"/>
              <MetricCard label="Latitude"           value={`${selected.lat}°N`} accentColor="var(--accent-cyan)"/>
              <MetricCard label="Longitude"          value={`${selected.lon}°`}  accentColor="var(--accent-blue)"/>
            </div>
            <div style={{marginTop:12,padding:'9px 14px',background:'rgba(239,68,68,0.06)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:'var(--radius)',fontFamily:'var(--font-mono)',fontSize:11,color:'#fca5a5'}}>
              I(D) = {selected.alpha} · D<sup style={{fontSize:9}}>{selected.beta}</sup>&nbsp;&nbsp;[I = mm/h, D = hours] &nbsp;—&nbsp; ECCC 2-year return period baseline
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-component: IDF Curves Log-Log Plot ──────────────────
function CanadaIDFCurvesTab({ selected }) {
  const [curves, setCurves]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [customAlpha, setCA]    = useState('');
  const [customBeta, setCB]     = useState('');
  const [postFire, setPF]       = useState(false);
  const [stormDur, setSD]       = useState('1');
  const [stormInt, setSI]       = useState('');
  const [csvData, setCsvData]   = useState(null);
  const [csvLoading, setCsvL]   = useState(false);
  const [activeRPs, setActiveRPs] = useState(new Set(['2-yr','10-yr','100-yr']));
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const fetchCurves = async () => {
    if (!selected) return;
    setLoading(true); setCurves(null);
    try {
      const body = {
        alpha: selected.alpha, beta: selected.beta,
        customAlpha: customAlpha || selected.alpha,
        customBeta: customBeta || selected.beta,
        postFire,
        stormDuration: stormDur ? parseFloat(stormDur) : null,
        stormIntensity: stormInt ? parseFloat(stormInt) : null,
      };
      const d = await (await fetch(`${API}/eccc-idf-curves`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)})).json();
      setCurves(d);
    } catch(e){console.error(e);}
    setLoading(false);
  };

  const fetchCSV = async () => {
    if (!selected) return;
    setCsvL(true); setCsvData(null);
    try {
      const d = await (await fetch(`${API}/eccc-daily-csv?stationId=${selected.id}&year=${new Date().getFullYear()}`)).json();
      setCsvData(d);
      if (d.maxEvent?.totalRain > 0) {
        setSI((d.maxEvent.totalRain / 24).toFixed(2));
        setSD('24');
      }
    } catch(e){}
    setCsvL(false);
  };

  // Rebuild chart whenever curves change
  useEffect(() => {
    if (!curves || !chartRef.current) return;
    if (chartInstance.current) { chartInstance.current.destroy(); }

    const datasets = [];
    // Return period curves
    curves.curves.forEach(rp => {
      if (!activeRPs.has(rp.label)) return;
      datasets.push({
        label: rp.label,
        data: rp.data.map(d => ({ x: d.duration, y: d.intensity })),
        borderColor: rp.color,
        backgroundColor: 'transparent',
        borderWidth: rp.dashed ? 1.5 : 2,
        borderDash: rp.dashed ? [6,3] : [],
        pointRadius: 0, tension: 0.3, fill: false
      });
    });
    // Custom threshold
    datasets.push({
      label: `Threshold ${postFire ? '(Post-Fire)' : '(Custom)'}`,
      data: curves.threshold.map(d => ({ x: d.duration, y: d.intensity })),
      borderColor: '#ef4444', backgroundColor: 'rgba(239,68,68,0.06)',
      borderWidth: 2.5, borderDash: [8,4],
      pointRadius: 0, tension: 0.3, fill: false
    });
    // Storm event point
    if (curves.stormResult) {
      const sr = curves.stormResult;
      const rc = { CRITICAL:'#ef4444', WARNING:'#f97316', WATCH:'#f59e0b', STABLE:'#22c55e', ADVISORY:'#38bdf8' }[sr.riskLevel] || '#94a3b8';
      datasets.push({
        label: `Storm Event (${sr.riskLevel})`,
        data: [{ x: sr.duration, y: sr.intensity }],
        borderColor: rc, backgroundColor: rc,
        pointRadius: 10, pointStyle: 'triangle',
        pointHoverRadius: 13, showLine: false, type: 'scatter'
      });
    }

    const ctx = chartRef.current.getContext('2d');
    chartInstance.current = new ChartJS(ctx, {
      type: 'line',
      data: { datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        animation: { duration: 400 },
        scales: {
          x: {
            type: 'logarithmic',
            title: { display: true, text: 'Duration (hours)', color: '#475569', font: { family: 'Space Mono', size: 9 } },
            ticks: {
              color: '#475569', font: { family: 'Space Mono', size: 9 },
              callback: (v) => {
                const labels = { 0.0833:'5min', 0.167:'10min', 0.25:'15min', 0.5:'30min', 1:'1h', 2:'2h', 3:'3h', 6:'6h', 12:'12h', 24:'24h' };
                return labels[parseFloat(v.toFixed(3))] || '';
              }
            },
            grid: { color: 'rgba(30,58,95,0.4)' }
          },
          y: {
            type: 'logarithmic',
            title: { display: true, text: 'Intensity (mm/h)', color: '#475569', font: { family: 'Space Mono', size: 9 } },
            ticks: { color: '#475569', font: { family: 'Space Mono', size: 9 }, callback: v => v % 1 === 0 || [0.5,5,50,500].includes(v) ? v : '' },
            grid: { color: 'rgba(30,58,95,0.4)' }
          }
        },
        plugins: {
          legend: { display: true, labels: { color: '#94a3b8', font: { size: 9, family: 'Space Mono' }, usePointStyle: true, boxWidth: 24 } },
          tooltip: { callbacks: {
            label: c => `${c.dataset.label}: ${typeof c.raw === 'object' ? `${c.raw.y?.toFixed(1)} mm/h @ ${c.raw.x}h` : c.parsed.y?.toFixed(1) + ' mm/h'}`
          }}
        }
      }
    });
    return () => { if (chartInstance.current) chartInstance.current.destroy(); };
  }, [curves, activeRPs, postFire]);

  if (!selected) return (
    <div className="panel" style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:300}}>
      <div style={{textAlign:'center',color:'var(--text-muted)'}}><span style={{fontSize:36,opacity:0.3,display:'block',marginBottom:10}}>🍁</span><div style={{fontSize:12,fontFamily:'var(--font-mono)'}}>Select a station on the Stations tab first</div></div>
    </div>
  );

  const allRPLabels = ['2-yr','5-yr','10-yr','25-yr','50-yr','100-yr'];
  const sr = curves?.stormResult;
  const rcColor = sr ? ({CRITICAL:'#ef4444',WARNING:'#f97316',WATCH:'#f59e0b',STABLE:'#22c55e',ADVISORY:'#38bdf8'}[sr.riskLevel]||'#94a3b8') : null;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      {/* Controls */}
      <div className="grid-2" style={{gap:14}}>
        <div className="panel">
          <div className="panel-header"><TrendingUp size={13} color="#ef4444"/><span className="panel-title">IDF Parameters — {selected.city}, {selected.province}</span></div>
          <div className="panel-body">
            <div style={{fontSize:10,fontFamily:'var(--font-mono)',color:'var(--text-muted)',marginBottom:8,letterSpacing:'0.06em',textTransform:'uppercase'}}>Return Periods to Plot</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:14}}>
              {allRPLabels.map(lbl => (
                <button key={lbl} onClick={() => setActiveRPs(s => { const n=new Set(s); n.has(lbl)?n.delete(lbl):n.add(lbl); return n; })}
                  style={{ padding:'3px 10px', borderRadius:20, fontSize:10, fontFamily:'var(--font-mono)', fontWeight:700, cursor:'pointer', border:`1px solid ${activeRPs.has(lbl)?RETURN_COLORS[lbl]:'var(--border)'}`, background:activeRPs.has(lbl)?`${RETURN_COLORS[lbl]}22`:'transparent', color:activeRPs.has(lbl)?RETURN_COLORS[lbl]:'var(--text-muted)' }}>
                  {lbl}
                </button>
              ))}
            </div>
            <div className="grid-2" style={{gap:10,marginBottom:10}}>
              <div><label className="form-label"><Tip id="alpha">Threshold α <span>[override]</span></Tip></label><input type="number" className="form-input" placeholder={`${selected.alpha}`} value={customAlpha} onChange={e=>setCA(e.target.value)}/></div>
              <div><label className="form-label"><Tip id="beta">Threshold β <span>[override]</span></Tip></label><input type="number" className="form-input" placeholder={`${selected.beta}`} value={customBeta} onChange={e=>setCB(e.target.value)}/></div>
            </div>
            <div className="toggle-row" style={{marginBottom:12}}>
              <div><div style={{fontSize:11,color:'var(--text-secondary)',fontWeight:600}}>Post-Fire Correction (α × 0.6)</div></div>
              <label className="toggle"><input type="checkbox" checked={postFire} onChange={e=>setPF(e.target.checked)}/><span className="toggle-slider"/></label>
            </div>
            <LoadingBtn loading={loading} label="Plot IDF Curves" loadingLabel="Calculating…" onClick={fetchCurves} icon={<TrendingUp size={13}/>}/>
          </div>
        </div>

        <div className="panel">
          <div className="panel-header"><CloudRain size={13} color="#f59e0b"/><span className="panel-title">Storm Event Input</span></div>
          <div className="panel-body">
            <div className="form-group">
              <label className="form-label"><Tip id="duration">Event Duration [hours]</Tip></label>
              <input type="number" className="form-input" value={stormDur} step={0.25} onChange={e=>setSD(e.target.value)}/>
            </div>
            <div className="form-group">
              <label className="form-label"><Tip id="intensity">Observed Intensity [mm/h]</Tip></label>
              <input type="number" className="form-input" placeholder="e.g. 18.5" value={stormInt} step={0.1} onChange={e=>setSI(e.target.value)}/>
            </div>
            <div className="sep" style={{margin:'10px 0'}}/>
            <div style={{fontSize:10,fontFamily:'var(--font-mono)',color:'var(--text-muted)',marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Auto-fill from ECCC Daily CSV</div>
            <LoadingBtn loading={csvLoading} label="Fetch ECCC Historical Data" loadingLabel="Fetching CSV…" onClick={fetchCSV} icon={<Download size={13}/>}/>
            {csvData && (
              <div style={{marginTop:10}}>
                {csvData.fallback ? (
                  <div className="alert alert-watch" style={{fontSize:10,marginTop:8}}><AlertTriangle size={11}/>{csvData.note}</div>
                ) : (
                  <div style={{fontSize:10,color:'var(--text-muted)',fontFamily:'var(--font-mono)',lineHeight:1.7}}>
                    <div>Events found: <span style={{color:'var(--accent-cyan)'}}>{csvData.totalEvents}</span></div>
                    <div>Max day: <span style={{color:'#f59e0b'}}>{csvData.maxEvent?.date}</span> — <span style={{color:'#ef4444'}}>{csvData.maxEvent?.totalRain} mm</span></div>
                    <div style={{color:'var(--text-muted)',fontSize:9,marginTop:3}}>Storm duration set to 24h · intensity = daily total ÷ 24</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Log-Log Chart */}
      <div className="panel">
        <div className="panel-header">
          <BarChart2 size={13} color="#ef4444"/>
          <span className="panel-title">IDF Curves — Log-Log Scale — {selected.city}, {selected.province}</span>
          <span style={{marginLeft:8,fontSize:9,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>ECCC Engineering Climate Datasets (2019) · 2-yr baseline: α={selected.alpha} β={selected.beta}</span>
        </div>
        <div className="panel-body">
          {!curves ? (
            <div style={{height:320,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text-muted)'}}>
              <div style={{textAlign:'center'}}><TrendingUp size={32} style={{margin:'0 auto 10px',opacity:0.2}}/><div style={{fontSize:12,fontFamily:'var(--font-mono)'}}>Configure parameters and click "Plot IDF Curves"</div></div>
            </div>
          ) : (
            <div style={{height:360}}><canvas ref={chartRef}/></div>
          )}
        </div>
      </div>

      {/* Storm Assessment */}
      {sr && (
        <div className="panel animate-in">
          <div className="panel-header"><AlertTriangle size={13} color={rcColor}/><span className="panel-title">Storm Event Assessment</span></div>
          <div className="panel-body">
            <div className={`alert ${sr.riskLevel==='CRITICAL'?'alert-critical':sr.riskLevel==='WARNING'?'alert-warning':sr.riskLevel==='WATCH'?'alert-watch':'alert-stable'}`} style={{marginBottom:12}}>
              {sr.isTriggered?<AlertTriangle size={20}/>:<CheckCircle size={20}/>}
              <div>
                <div style={{fontWeight:700,fontSize:14,marginBottom:2}}>SYSTEM {sr.riskLevel}{postFire&&<span style={{marginLeft:8,fontSize:10,opacity:0.8}}>🔥 POST-FIRE</span>}</div>
                <div style={{fontSize:11}}>{sr.intensity} mm/h over {sr.duration}h — Threshold: {sr.thresholdAtDuration} mm/h · Exceedance: <strong>{sr.exceedanceRatio}×</strong>
                  {sr.exceededReturnPeriod && <span style={{marginLeft:8,color:RETURN_COLORS[`${sr.exceededReturnPeriod}-yr`]||'#f59e0b'}}> Exceeds {sr.exceededReturnPeriod}-year return period</span>}
                </div>
              </div>
            </div>
            <div className="grid-3" style={{gap:10}}>
              <MetricCard label="Exceedance Ratio" value={sr.exceedanceRatio} accentColor={sr.isTriggered?'#ef4444':'var(--accent-green)'} tipId="exceedance"/>
              <MetricCard label="Safety Factor"     value={sr.safetyFactor}   accentColor="var(--accent-cyan)"    tipId="safetyFactor"/>
              <MetricCard label="Return Period"     value={sr.exceededReturnPeriod ? `>${sr.exceededReturnPeriod}-yr` : '<2-yr'} accentColor="#f59e0b"/>
            </div>
          </div>
        </div>
      )}

      {/* Return period table */}
      {curves && (
        <div className="panel animate-in">
          <div className="panel-header"><Info size={13} color="var(--text-muted)"/><span className="panel-title">Intensity Table at Standard Durations — {selected.city}</span></div>
          <div className="panel-body" style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:10,fontFamily:'var(--font-mono)'}}>
              <thead>
                <tr>
                  <th style={{background:'var(--bg-elevated)',padding:'6px 10px',textAlign:'left',color:'var(--text-muted)',borderBottom:'1px solid var(--border)',fontWeight:700}}>Duration</th>
                  {curves.curves.map(rp=>(
                    <th key={rp.label} style={{background:'var(--bg-elevated)',padding:'6px 10px',textAlign:'center',color:rp.color,borderBottom:'1px solid var(--border)',fontWeight:700}}>{rp.label}</th>
                  ))}
                  <th style={{background:'var(--bg-elevated)',padding:'6px 10px',textAlign:'center',color:'#ef4444',borderBottom:'1px solid var(--border)',fontWeight:700}}>Threshold</th>
                </tr>
              </thead>
              <tbody>
                {curves.curves[0].data.map((_, i) => (
                  <tr key={i} style={{background:i%2===0?'var(--bg-elevated)':'var(--bg-base)'}}>
                    <td style={{padding:'5px 10px',color:'var(--accent-cyan)',borderBottom:'1px solid rgba(30,58,95,0.3)'}}>{curves.durationLabels[i]}</td>
                    {curves.curves.map(rp=>(
                      <td key={rp.label} style={{padding:'5px 10px',textAlign:'center',color:'var(--text-secondary)',borderBottom:'1px solid rgba(30,58,95,0.3)'}}>{rp.data[i].intensity}</td>
                    ))}
                    <td style={{padding:'5px 10px',textAlign:'center',color:'#ef4444',borderBottom:'1px solid rgba(30,58,95,0.3)',fontWeight:700}}>{curves.threshold[i].intensity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{fontSize:9,color:'var(--text-muted)',fontFamily:'var(--font-mono)',marginTop:8}}>
              Intensities in mm/h. Return period scaling: Gumbel distribution ratios applied to ECCC 2-yr baseline. Source: ECCC Engineering Climate Datasets (2019). For infrastructure design use the station-specific ECCC IDF tables at climate.weather.gc.ca
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-component: Live ECCC Weather ────────────────────────
function CanadaLiveTab({ selected }) {
  const [eccc, setEccc]         = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [polling, setPolling]   = useState(false);
  const intervalRef             = useRef(null);

  const fetchLive = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const d = await (await fetch(`${API}/eccc-weather?lat=${selected.lat}&lon=${selected.lon}`)).json();
      setEccc(d);
      const intensity = d.precip1h ?? 0;
      const a = await (await fetch(`${API}/analyze-rainfall`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({intensity,duration:1,alpha:d.idf?.alpha||selected.alpha,beta:d.idf?.beta||selected.beta,postFire:false})})).json();
      setAnalysis(a);
    } catch(e){console.error(e);}
    setLoading(false);
  }, [selected]);

  const togglePolling = () => {
    if (polling) { clearInterval(intervalRef.current); setPolling(false); }
    else { fetchLive(); intervalRef.current=setInterval(fetchLive,10*60*1000); setPolling(true); }
  };
  useEffect(() => () => clearInterval(intervalRef.current), []);

  if (!selected) return (
    <div className="panel" style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:200}}>
      <div style={{textAlign:'center',color:'var(--text-muted)'}}><span style={{fontSize:32,opacity:0.3,display:'block',marginBottom:10}}>🍁</span><div style={{fontSize:12,fontFamily:'var(--font-mono)'}}>Select a station first</div></div>
    </div>
  );

  const rc = analysis ? (RISK_COLORS[analysis.riskLevel]||'#94a3b8') : null;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <div className="panel">
        <div className="panel-header"><Radio size={13} color="var(--text-accent)"/><span className="panel-title">ECCC MSC Live Observation — {selected.city}, {selected.province}</span></div>
        <div className="panel-body">
          <div className="alert alert-info" style={{marginBottom:12,fontSize:11}}><Info size={12}/>
            <span>Data from <strong>ECCC MSC SWOB-ML API</strong> — fully public, no key required. Uses ECCC IDF parameters (α={selected.alpha}, β={selected.beta}) for threshold calculation.</span>
          </div>
          <div className="grid-2" style={{gap:10,marginBottom:12}}>
            <button className={`btn ${polling?'btn-danger':'btn-success'} btn-full`} onClick={togglePolling}>
              {polling?<><span className="spinner"/>Stop Live Monitor</>:<><Radio size={13}/>Start Live Monitor (10-min)</>}
            </button>
            <button className="btn btn-secondary btn-full" onClick={fetchLive} disabled={loading}>
              <RefreshCw size={13}/>Manual Fetch
            </button>
          </div>
          {eccc && (
            <div className="animate-in">
              <div className="live-monitor">
                <div className="live-header">
                  <div>
                    <div style={{fontFamily:'var(--font-mono)',fontSize:12,fontWeight:700,color:'var(--text-primary)'}}>📍 {eccc.stationName || eccc.nearestStation?.name || selected.name}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>
                      {eccc.live
                        ? `Updated: ${eccc.obsTime ? new Date(eccc.obsTime).toLocaleString() : 'recent'}${eccc.obsDistanceKm !== undefined ? ` · Obs station ${eccc.obsDistanceKm} km away` : ''}`
                        : '⚠ Fallback — IDF parameters only'}
                    </div>
                  </div>
                  {polling&&<div className="live-pulse"><span className="status-dot"/>LIVE</div>}
                </div>
                {eccc.fallback && <div className="alert alert-watch" style={{marginBottom:12,fontSize:11}}><AlertTriangle size={11}/>{eccc.fallbackReason}</div>}
                {eccc.live && (
                  <div className="grid-4" style={{gap:9,marginBottom:10}}>
                    {eccc.precip1h!==null && <MetricCard label="Precip 1h" value={eccc.precip1h} unit="mm/h" accentColor="var(--accent-cyan)"/>}
                    {eccc.precip24h!==null && <MetricCard label="Precip 24h" value={eccc.precip24h} unit="mm" accentColor="var(--accent-blue)"/>}
                    {eccc.temperature!==null && <MetricCard label="Temp" value={`${eccc.temperature}°`} unit="C" accentColor="var(--accent-orange)"/>}
                    {eccc.windSpeed!==null && <MetricCard label="Wind" value={eccc.windSpeed} unit="m/s" accentColor="var(--accent-violet)"/>}
                    {eccc.humidity!==null && <MetricCard label="Humidity" value={`${eccc.humidity}%`} accentColor="var(--text-muted)"/>}
                    {eccc.pressure!==null && <MetricCard label="Pressure" value={eccc.pressure} unit="hPa" accentColor="var(--accent-blue)"/>}
                  </div>
                )}
                {analysis && (
                  <div className={`alert ${analysis.riskLevel==='CRITICAL'?'alert-critical':analysis.riskLevel==='WARNING'?'alert-warning':analysis.riskLevel==='WATCH'?'alert-watch':'alert-stable'}`}>
                    {analysis.isTriggered?<AlertTriangle size={16}/>:<CheckCircle size={16}/>}
                    <div>
                      <div style={{fontWeight:700,fontSize:13}}>Hazard: {analysis.riskLevel}</div>
                      <div style={{fontSize:11}}>{eccc.precip1h??0} mm/h vs ECCC threshold {analysis.criticalIntensity} mm/h · ER: {analysis.exceedanceRatio}×</div>
                    </div>
                  </div>
                )}
              </div>
              {analysis && (
                <div className="panel" style={{marginTop:12}}>
                  <div className="panel-header"><TrendingUp size={12} color="var(--text-muted)"/><span className="panel-title">I-D Assessment (1h) — Canadian ECCC Parameters</span></div>
                  <div className="panel-body" style={{padding:'10px 14px'}}>
                    {[['Observed (1h)',`${eccc.precip1h??0} mm/h`,null],
                      ['Critical Threshold',`${analysis.criticalIntensity} mm/h`,null],
                      ['Exceedance Ratio',`${analysis.exceedanceRatio}×`,'exceedance'],
                      ['Safety Factor',analysis.safetyFactor,'safetyFactor'],
                      ['ECCC Station α',`${eccc.idf?.alpha||selected.alpha}`,null],
                      ['ECCC Station β',`${eccc.idf?.beta||selected.beta}`,null],
                      ['Nearest IDF Station',`${eccc.nearestIDF?.name||selected.name} (${eccc.nearestIDF?.distanceKm||0} km)`,null],
                      ['Debris Flow Risk',analysis.isTriggered?'⚠ THRESHOLD EXCEEDED':'✓ Below threshold',null],
                    ].map(([k,v,t])=>(
                      <div className="stat-row" key={k}><span className="stat-key">{t?<Tip id={t}>{k}</Tip>:k}</span><span className="stat-val" style={{color:k==='Debris Flow Risk'?rc:undefined}}>{v}</span></div>
                    ))}
                    <div style={{marginTop:10,padding:'8px 12px',background:'rgba(239,68,68,0.06)',border:'1px solid rgba(239,68,68,0.16)',borderRadius:'var(--radius)',fontSize:10,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>
                      I<sub>c</sub>(1h) = {eccc.idf?.alpha||selected.alpha} · 1^({eccc.idf?.beta||selected.beta}) = {analysis.criticalIntensity} mm/h&nbsp;—&nbsp;ECCC Engineering Climate Datasets (2019), 2-yr return period
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {!eccc && !loading && (
            <div style={{textAlign:'center',color:'var(--text-muted)',padding:'30px 0'}}>
              <Radio size={32} style={{margin:'0 auto 10px',opacity:0.2}}/>
              <div style={{fontSize:12,fontFamily:'var(--font-mono)'}}>Click "Start Live Monitor" or "Manual Fetch"</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-component: Trigger Assessment (Canadian-calibrated I-D) ──
function CanadaTriggerTab({ selected }) {
  const [params, setParams] = useState({
    duration: 3, intensity: 18, postFire: false, region: 'Canadian ECCC'
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Auto-fill alpha/beta from selected station whenever it changes
  useEffect(() => {
    if (selected) {
      setResult(null); // reset on station change
    }
  }, [selected]);

  const effectiveAlpha = selected ? selected.alpha : 14.82;
  const effectiveBeta  = selected ? selected.beta  : -0.39;
  const stationLabel   = selected ? `${selected.city}, ${selected.province}` : 'Global baseline';

  const run = async () => {
    setLoading(true);
    try {
      const body = {
        intensity: params.intensity,
        duration:  params.duration,
        alpha:     params.postFire ? effectiveAlpha * 0.6 : effectiveAlpha,
        beta:      effectiveBeta,
        postFire:  params.postFire,
        region:    stationLabel,
      };
      const data = await (await fetch(`${API}/analyze-rainfall`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })).json();
      setResult(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // Build I-D chart using same Caine-style curve but with Canadian α/β
  const buildChart = (tc, ud, ui) => {
    if (!tc) return null;
    const exceeded = ui >= (tc.find(p => p.duration === ud)?.threshold || 0);
    const idx = tc.findIndex(p => p.duration === ud);
    return {
      labels: tc.map(p => `${p.duration}h`),
      datasets: [
        {
          label: `I-D Threshold — ${stationLabel}`,
          data: tc.map(p => p.threshold),
          borderColor: '#ef4444', backgroundColor: 'rgba(239,68,68,0.06)',
          borderWidth: 2, tension: 0.4, fill: true,
          pointRadius: tc.map((_, i) => i === idx ? 10 : 3),
          pointStyle:  tc.map((_, i) => i === idx ? 'triangle' : 'circle'),
          pointBackgroundColor: tc.map((_, i) =>
            i === idx ? (exceeded ? 'rgba(239,68,68,0.95)' : 'rgba(34,197,94,0.95)')
                      : 'rgba(239,68,68,0.6)'),
          pointBorderColor: tc.map((_, i) => i === idx ? '#fff' : 'transparent'),
          pointBorderWidth: tc.map((_, i) => i === idx ? 1.5 : 0),
        },
        {
          label: 'Global Baseline (Caine 1980)',
          data: tc.map(p => parseFloat((14.82 * Math.pow(Math.max(0.001, p.duration), -0.39)).toFixed(2))),
          borderColor: 'rgba(56,189,248,0.5)', backgroundColor: 'transparent',
          borderWidth: 1.5, borderDash: [4, 4], tension: 0.4, fill: false,
          pointRadius: 0,
        },
        {
          label: 'Observed Event',
          data: tc.map((_, i) => i === idx ? ui : null),
          borderColor: 'transparent',
          backgroundColor: exceeded ? 'rgba(239,68,68,0.9)' : 'rgba(34,197,94,0.9)',
          pointRadius: tc.map((_, i) => i === idx ? 12 : 0),
          pointStyle: 'triangle', showLine: false,
        }
      ]
    };
  };

  const alertClass = {
    CRITICAL: 'alert-critical', WARNING: 'alert-warning',
    WATCH: 'alert-watch', ADVISORY: 'alert-info', STABLE: 'alert-stable'
  }[result?.riskLevel] || 'alert-stable';

  const rc = result ? (RISK_COLORS[result.riskLevel] || '#94a3b8') : null;

  return (
    <div className="animate-in">
      {/* Station context banner */}
      <div style={{
        background: selected ? 'rgba(239,68,68,0.08)' : 'rgba(56,189,248,0.06)',
        border: `1px solid ${selected ? 'rgba(239,68,68,0.25)' : 'rgba(56,189,248,0.2)'}`,
        borderRadius: 'var(--radius-lg)', padding: '12px 16px', marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 12
      }}>
        <span style={{ fontSize: 18 }}>🍁</span>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: selected ? '#fca5a5' : 'var(--text-accent)', fontFamily: 'var(--font-mono)' }}>
            {selected ? `Using ECCC IDF Parameters — ${stationLabel}` : 'No station selected — using Global Caine (1980) baseline'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
            α = {params.postFire ? (effectiveAlpha * 0.6).toFixed(2) : effectiveAlpha}
            &nbsp;&nbsp;β = {effectiveBeta}
            &nbsp;&nbsp;{params.postFire ? '🔥 Post-Fire correction applied (α × 0.6)' : ''}
            {!selected && ' → Select a station on the Stations tab for Canadian-calibrated thresholds'}
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="panel">
          <div className="panel-header">
            <AlertTriangle size={13} color="#ef4444" />
            <span className="panel-title">Storm Event Input</span>
          </div>
          <div className="panel-body">
            {[
              ['Event Duration', 'duration', 'h', 'duration', 1],
              ['Observed Intensity', 'intensity', 'mm/h', 'intensity', 0.1],
            ].map(([label, key, unit, tipId, step]) => (
              <div className="form-group" key={key}>
                <label className="form-label">
                  <Tip id={tipId}>{label} <span>[{unit}]</span></Tip>
                </label>
                <input type="number" className="form-input"
                  value={params[key]} step={step}
                  onChange={e => setParams(p => ({ ...p, [key]: parseFloat(e.target.value) }))} />
              </div>
            ))}

            <div className="toggle-row">
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>Post-Fire Correction</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Applies 40% reduction to α (α × 0.6)</div>
              </div>
              <label className="toggle">
                <input type="checkbox" checked={params.postFire}
                  onChange={e => setParams(p => ({ ...p, postFire: e.target.checked }))} />
                <span className="toggle-slider" />
              </label>
            </div>

            {/* Station α/β display */}
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 12px', marginTop: 12, marginBottom: 14 }}>
              <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                Threshold equation being used
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#fca5a5' }}>
                I<sub>c</sub> = {params.postFire ? (effectiveAlpha * 0.6).toFixed(2) : effectiveAlpha} × D<sup style={{ fontSize: 9 }}>{effectiveBeta}</sup>
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                {selected
                  ? `ECCC Engineering Climate Datasets (2019) — ${stationLabel}`
                  : 'Caine (1980) global lower-bound threshold'}
              </div>
            </div>

            <LoadingBtn loading={loading} label="Assess Debris Flow Trigger" loadingLabel="Analyzing…"
              onClick={run} icon={<AlertTriangle size={13} />} />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {result ? (
            <>
              <div className={`alert ${alertClass} animate-in`}>
                {result.isTriggered ? <AlertTriangle size={22} /> : <CheckCircle size={22} />}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>
                    SYSTEM {result.riskLevel}
                    {result.postFire && <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.8 }}>🔥 POST-FIRE</span>}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.85 }}>
                    {result.intensity} mm/h — Canadian Threshold ({stationLabel}): {result.criticalIntensity} mm/h
                    &nbsp;·&nbsp;Exceedance: <strong>{result.exceedanceRatio}×</strong>
                  </div>
                </div>
              </div>

              <div className="grid-3" style={{ gap: 9 }}>
                <MetricCard label="Exceedance Ratio" value={result.exceedanceRatio}
                  accentColor={result.isTriggered ? '#ef4444' : 'var(--accent-green)'} tipId="exceedance" />
                <MetricCard label="Safety Factor" value={result.safetyFactor}
                  accentColor="var(--accent-cyan)" tipId="safetyFactor" />
                <MetricCard label="Critical I" value={result.criticalIntensity}
                  unit="mm/h" accentColor="#ef4444" tipId="intensity" />
              </div>

              <div className="panel animate-in">
                <div className="panel-header">
                  <TrendingUp size={13} color="#ef4444" />
                  <span className="panel-title">Canadian Intensity-Duration Threshold Curve</span>
                </div>
                <div className="panel-body">
                  <div className="chart-container" style={{ height: 230 }}>
                    {result.thresholdCurve && (
                      <Line
                        data={buildChart(result.thresholdCurve, result.duration, result.intensity)}
                        options={{
                          responsive: true, maintainAspectRatio: false, animation: { duration: 400 },
                          plugins: {
                            legend: { display: true, labels: { color: '#94a3b8', font: { size: 9, family: 'Space Mono' } } },
                            tooltip: { callbacks: { label: c => `${c.dataset.label}: ${c.raw} mm/h` } }
                          },
                          scales: {
                            x: { grid: { color: 'rgba(30,58,95,0.5)' }, ticks: { color: '#475569', font: { family: 'Space Mono', size: 9 } }, title: { display: true, text: 'Duration (hours)', color: '#475569', font: { size: 9 } } },
                            y: { grid: { color: 'rgba(30,58,95,0.5)' }, ticks: { color: '#475569', font: { family: 'Space Mono', size: 9 } }, title: { display: true, text: 'Intensity (mm/h)', color: '#475569', font: { size: 9 } } }
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Step-by-step breakdown */}
              <div style={{ background: 'var(--bg-elevated)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-lg)', padding: '12px 16px', fontSize: 11, fontFamily: 'var(--font-mono)', lineHeight: 2 }}>
                <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#ef4444', marginBottom: 6, fontWeight: 700 }}>Calculation Breakdown</div>
                {[
                  ['1. Effective α', `${params.postFire ? (effectiveAlpha*0.6).toFixed(2) : effectiveAlpha}${params.postFire ? ` (${effectiveAlpha} × 0.6 post-fire)` : ` (ECCC ${stationLabel})`}`],
                  ['2. Critical Intensity', `I_c = ${params.postFire ? (effectiveAlpha*0.6).toFixed(2) : effectiveAlpha} × ${params.duration}^(${effectiveBeta}) = ${result.criticalIntensity} mm/h`],
                  ['3. Exceedance Ratio',   `ER = ${result.intensity} / ${result.criticalIntensity} = ${result.exceedanceRatio}×`],
                  ['4. Safety Factor',      `SF = 1 / ${result.exceedanceRatio} = ${result.safetyFactor}`],
                  ['5. Risk Level',         result.riskLevel + (result.isTriggered ? ' — threshold exceeded' : ' — below threshold')],
                ].map(([k, v]) => (
                  <div key={k} className="stat-row">
                    <span className="stat-key">{k}</span>
                    <span className="stat-val" style={{ color: k === '5. Risk Level' ? rc : 'var(--accent-cyan)', fontSize: 10 }}>{v}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="panel" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
              <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                <AlertTriangle size={32} style={{ margin: '0 auto 10px', opacity: 0.2 }} />
                <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)' }}>
                  {selected ? `Ready to assess using ${stationLabel} parameters` : 'Enter storm data and assess'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main AnalyzeCanadian wrapper ─────────────────────────────
function AnalyzeCanadian() {
  const [canTab, setCanTab] = useState('stations');
  const [selected, setSelected] = useState(null);

  const tabs = [
    { id:'stations', label:'📍 Stations',          tip:'Find & Browse ECCC IDF Stations' },
    { id:'curves',   label:'📊 IDF Curves',         tip:'Log-log chart, return periods, threshold, storm event' },
    { id:'trigger',  label:'⚠️ Trigger Assessment', tip:'Canadian-calibrated I-D debris flow trigger analysis' },
    { id:'live',     label:'⚡ Live ECCC',          tip:'Real-time ECCC SWOB + hazard assessment' },
  ];

  return (
    <div className="animate-in">
      <div className="section-header">
        <div className="section-icon" style={{background:'rgba(220,38,38,0.12)'}}><span style={{fontSize:20}}>🍁</span></div>
        <div>
          <div className="section-title">Canadian IDF &amp; Debris Flow Trigger Analysis</div>
          <div className="section-desc">
            ECCC Engineering Climate Datasets (2019) · 37 stations · Multi-return-period IDF curves ·
            Canadian-calibrated trigger assessment · SWOB-ML real-time · No API key required
            {selected && <span style={{marginLeft:10,color:'var(--accent-cyan)',fontFamily:'var(--font-mono)',fontSize:11}}>▸ {selected.city}, {selected.province} (α={selected.alpha} β={selected.beta})</span>}
          </div>
        </div>
      </div>

      {/* Child tabs */}
      <div style={{display:'flex',gap:2,borderBottom:'1px solid var(--border)',marginBottom:18}}>
        {tabs.map(t => (
          <button key={t.id} title={t.tip} onClick={() => setCanTab(t.id)} style={{
            padding:'8px 16px', border:'none', background:'transparent', cursor:'pointer',
            fontFamily:'var(--font-mono)', fontSize:11, fontWeight:700,
            letterSpacing:'0.04em', textTransform:'uppercase',
            color: canTab===t.id ? 'var(--accent-cyan)' : 'var(--text-muted)',
            borderBottom: canTab===t.id ? '2px solid var(--accent-cyan)' : '2px solid transparent',
            marginBottom:-1, transition:'all 0.15s'
          }}>{t.label}</button>
        ))}
      </div>

      {canTab === 'stations' && <CanadaStationsTab selected={selected} setSelected={setSelected}/>}
      {canTab === 'curves'   && <CanadaIDFCurvesTab selected={selected}/>}
      {canTab === 'trigger'  && <CanadaTriggerTab selected={selected}/>}
      {canTab === 'live'     && <CanadaLiveTab selected={selected}/>}
    </div>
  );
}


function LearnCanadian() {
  return (
    <div className="learn-layout">
      <div className="learn-hero">
        <div className="learn-hero-title">🍁 Canadian IDF Data — ECCC Engineering Climate Datasets</div>
        <div className="learn-hero-author">Environment and Climate Change Canada (ECCC) · 2nd Generation IDF v3.10, 2022</div>
        <div className="learn-hero-desc">
          Canada's national IDF database covers 700+ stations nationwide, freely published at <strong>climate.weather.gc.ca/prods_servs/engineering_e.html</strong>.
          The datasets provide intensity-duration-frequency curves for return periods of 2–100 years using Gumbel or GEV distributions
          fitted to Annual Maximum Series (AMS) precipitation records spanning 30–100 years per station.
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><TrendingUp size={15} color="#ef4444"/><span className="learn-card-title">What is IDF in Hydrology?</span></div>
        <div className="learn-card-body">
          <div className="learn-section">
            <div className="learn-section-title">Intensity-Duration-Frequency Relationship</div>
            <div className="learn-text">IDF curves relate three critical rainfall variables: <strong>Intensity</strong> (mm/h), <strong>Duration</strong> (hours), and <strong>Frequency</strong> (return period in years). They are plotted on log-log axes where each curve represents one return period, and intensity decreases with increasing duration.</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Power-Law IDF Formula</div>
              I(D, T) = α_T · D^β_T     [I = mm/h, D = hours, T = return period]<br/><br/>
              α_T = α_2yr · F_T    (Gumbel scaling factor)<br/>
              β_T ≈ β_2yr          (exponent relatively stable across return periods)<br/><br/>
              For debris flow assessment:  α and β are set to the 2-year baseline (Caine threshold)
            </div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">How to Use ECCC IDF for Debris Flow Analysis</div>
            <div className="learn-text"><strong>1. Establish threshold:</strong> Compare a known debris flow event against the ECCC IDF curve. If a 1-in-50-year storm triggered a flow, set that curve as the critical threshold.</div>
            <div className="learn-text"><strong>2. Determine return period of trigger event:</strong> Plot observed storm intensity + duration on the IDF graph to read off the exceedance return period. A 1-in-5-year trigger indicates extremely high site risk.</div>
            <div className="learn-text"><strong>3. Climate change adjustment:</strong> ECCC recommends scaling intensities by ~7% per °C of warming. Use the IDF_CC Tool at <strong>idf-cc-tool.ca</strong> for future projections.</div>
            <div className="learn-highlight">Pro Tip: For debris flow analysis, <strong>short durations (5–60 min)</strong> represent high-intensity convective burst triggers (stony flows), while <strong>long durations (12–24 h)</strong> reflect groundwater buildup for viscous/pore-pressure failures. Use both in your assessment.</div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Return Period Scaling (Gumbel Distribution)</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Gumbel Intensity Ratios (T-year / 2-year)</div>
              2-yr:   1.000×   (baseline)<br/>
              5-yr:   1.280×<br/>
              10-yr:  1.490×<br/>
              25-yr:  1.740×<br/>
              50-yr:  1.950×<br/>
              100-yr: 2.160×<br/><br/>
              Equivalent to:  I_T = α_2yr · F_T · D^β
            </div>
          </div>
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><Globe size={15} color="#ef4444"/><span className="learn-card-title">Canadian Regional Debris Flow Thresholds — All Provinces &amp; Territories</span></div>
        <div className="learn-card-body">
          <div className="learn-text">Regional thresholds derived from ECCC Engineering Climate Datasets, provincial geotechnical studies, and published literature. Values represent the 2-year return period power-law baseline I = α · D^β for debris flow initiation assessment.</div>
          <table className="learn-threshold-table">
            <thead><tr><th>Region</th><th>Province(s)</th><th>α</th><th>β</th><th>Source / Notes</th></tr></thead>
            <tbody>
              {CANADA_THRESHOLDS.map((t,i) => (
                <tr key={i}>
                  <td style={{fontWeight:t.province==='—'?400:600,color:t.province==='—'?'var(--text-muted)':'var(--text-secondary)'}}>{t.region}</td>
                  <td style={{fontFamily:'var(--font-mono)',fontSize:10,color:'var(--accent-cyan)'}}>{t.province}</td>
                  <td style={{fontFamily:'var(--font-mono)',fontWeight:700,color:'#ef4444'}}>{t.alpha}</td>
                  <td style={{fontFamily:'var(--font-mono)',fontWeight:700,color:'#f59e0b'}}>{t.beta}</td>
                  <td style={{fontSize:10,color:'var(--text-muted)'}}>{t.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><MapPin size={15} color="#ef4444"/><span className="learn-card-title">ECCC Data Sources &amp; API Access</span></div>
        <div className="learn-card-body">
          <div className="learn-section">
            <div className="learn-section-title">Engineering Climate Datasets (IDF Parameters)</div>
            <div className="learn-eq-block">
              <div className="learn-eq-label">Data Access URLs</div>
              IDF Files (v3.10):  climate.weather.gc.ca/prods_servs/engineering_e.html<br/>
              Open Government Portal: open.canada.ca (search "IDF")<br/>
              Daily CSV Datamart:  dd.weather.gc.ca/climate/observations/daily/csv/{"{PROV}"}/<br/>
              SWOB Real-time:     api.weather.gc.ca/collections/swob-realtime/items<br/>
              IDF_CC Tool:        idf-cc-tool.ca (climate change projections)
            </div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Daily CSV Format (dd.weather.gc.ca)</div>
            <div className="learn-text">ECCC daily CSVs use filename pattern:</div>
            <div className="learn-eq-block">climate_daily_{"{PROV}"}_{"{stationId}"}_{"{year}"}_P1D.csv</div>
            <div className="learn-text">Key columns for debris flow analysis:</div>
            <div className="learn-vars-grid">
              <span className="learn-var-sym">Total Rain (mm)</span><span className="learn-var-def">Daily liquid precipitation — primary debris flow trigger variable</span>
              <span className="learn-var-sym">Total Precip (mm)</span><span className="learn-var-def">Total precipitation including snow — use for snowmelt analysis</span>
              <span className="learn-var-sym">Max Temp (°C)</span><span className="learn-var-def">Useful for snowmelt + rain-on-snow events (significant in AB/BC Rockies)</span>
            </div>
          </div>
          <div className="learn-section">
            <div className="learn-section-title">Regional Climate Notes for Debris Flow</div>
            <table className="learn-threshold-table">
              <thead><tr><th>Region</th><th>Primary Trigger</th><th>Notable IDF Characteristic</th></tr></thead>
              <tbody>
                <tr><td>Coastal BC</td><td>Frontal winter storms (Nov–Mar)</td><td>Highest long-duration intensity in Canada; orographic enhancement on Coast/Cascade</td></tr>
                <tr><td>Interior BC / AB Rockies</td><td>Summer convection + orographic</td><td>Short, intense events; Banff/Golden area historically high debris flow frequency</td></tr>
                <tr><td>AB / SK Prairies</td><td>Summer convective cells</td><td>Moderate α; soil infiltration varies with antecedent moisture</td></tr>
                <tr><td>ON Great Lakes</td><td>Lake-effect + convection</td><td>Highest Ontario IDF near Lake Ontario/Erie shoreline</td></tr>
                <tr><td>QC St. Lawrence / Laurentians</td><td>Frontal + convective</td><td>Saguenay 1996 flood event (record rainfall) shaped regional thresholds</td></tr>
                <tr><td>Maritimes (NB/NS/PE)</td><td>Frontal + tropical remnants</td><td>Hurricane season (Jul–Oct) elevates exceedance risk significantly</td></tr>
                <tr><td>Newfoundland / NL</td><td>Extratropical cyclones</td><td>Highest α in Canada; St. John's receives frequent intense events</td></tr>
                <tr><td>YT / NT / NU</td><td>Prolonged low-intensity</td><td>Low α but permafrost dramatically reduces infiltration → rapid surface runoff</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="learn-card">
        <div className="learn-card-header"><BookOpen size={15} color="#475569"/><span className="learn-card-title">Key References</span></div>
        <div className="learn-card-body">
          <ul className="learn-ref-list">
            <li className="learn-ref-item"><b>Environment and Climate Change Canada (2022).</b> Engineering Climate Datasets v3.10 — IDF Files. climate.weather.gc.ca/prods_servs/engineering_e.html</li>
            <li className="learn-ref-item"><b>Meteorological Service of Canada (2024).</b> MSC GeoMet — SWOB-ML Real-Time Surface Observations. api.weather.gc.ca</li>
            <li className="learn-ref-item"><b>Mailhot, A. et al. (2007).</b> Assessment of future change in IDF curves for Southern Quebec using the Canadian Regional Climate Model. Journal of Hydrology, 347, 197–210.</li>
            <li className="learn-ref-item"><b>Shephard, M.W. et al. (2014).</b> Development of a long-term, consistent hourly precipitation dataset for climate studies over Canada. Journal of Climate, 27(2), 664–678.</li>
            <li className="learn-ref-item"><b>Jakob, M. et al. (2006).</b> Regional debris flow frequency and hazard assessment on the Queen Charlotte Islands, BC. Natural Hazards, 38, 529–552.</li>
            <li className="learn-ref-item"><b>Porter, M. &amp; Morgenstern, N. (2013).</b> Landslide risk evaluation — Canadian Technical Guidelines. Open File 7315, Geological Survey of Canada.</li>
            <li className="learn-ref-item"><b>Caine, N. (1980).</b> The rainfall intensity-duration control of shallow landslides and debris flows. Geografiska Annaler, 62A(1–2), 23–27.</li>
            <li className="learn-ref-item"><b>IDF_CC Tool (2024).</b> Climate-change-adjusted IDF curves for Canadian stations. idf-cc-tool.ca — Mailhot et al. methodology.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// ROOT APP
// ═══════════════════════════════════════════════════════════
const MODULES = [
  { id:'basic',    label:'Basic Analysis',    sub:'Takahashi Model',     icon:Activity,   color:'#0ea5e9', analyze:AnalyzeTakahashi, learn:LearnTakahashi  },
  { id:'mc',       label:'Monte Carlo',       sub:'Probabilistic Sim',   icon:BarChart2,  color:'#a855f7', analyze:AnalyzeMonteCarlo,learn:LearnMonteCarlo },
  { id:'dem',      label:'DEM Analysis',      sub:'Terrain Processing',  icon:Mountain,   color:'#10b981', analyze:AnalyzeDEM,       learn:LearnDEM        },
  { id:'rainfall', label:'Precip Trigger',    sub:'Global I-D Threshold',icon:CloudRain,  color:'#f59e0b', analyze:AnalyzeRainfall,  learn:LearnRainfall   },
  { id:'live',     label:'Live Monitor',      sub:'Real-time Hazard',    icon:Radio,      color:'#38bdf8', analyze:AnalyzeWeather,   learn:LearnWeather    },
  { id:'canada',   label:'Canadian IDF',      sub:'ECCC + Trigger',      icon:MapPin,     color:'#ef4444', analyze:AnalyzeCanadian,  learn:LearnCanadian   }
];

export default function App() {
  const [activeModule, setActiveModule] = useState('basic');
  const [activeSubTab, setActiveSubTab] = useState('analyze');
  const mod = MODULES.find(m => m.id === activeModule);
  const ActiveComponent = activeSubTab === 'analyze' ? mod?.analyze : mod?.learn;

  const handleModuleClick = (id) => {
    setActiveModule(id);
    setActiveSubTab('analyze');
  };

  return (
    <div className="app-shell">
      {/* TOPBAR */}
      <div className="topbar">
        <div className="topbar-brand">
          <div className="logo-icon">⛰</div>
          <div>
            <div className="logo-text">GeoHazard Suite</div>
            <div className="logo-sub">Debris Flow Analysis v12.0</div>
          </div>
        </div>
        <div className="topbar-right">
          <div className="topbar-hint">
            Hover <HelpCircle size={10} style={{display:'inline',verticalAlign:'middle',color:'#38bdf8'}}/> for equations
          </div>
          <div className="topbar-status">
            <span className="status-dot"/>Engine Online
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="body-layout">
        {/* SIDEBAR */}
        <div className="sidebar">
          <div className="sidebar-section-label">Analysis Modules</div>
          <nav className="sidebar-nav">
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  className={`sidebar-btn${activeModule === m.id ? ' active' : ''}`}
                  onClick={() => handleModuleClick(m.id)}
                >
                  <div className="sidebar-btn-icon">
                    <Icon size={15} color={activeModule === m.id ? m.color : 'var(--text-muted)'} />
                  </div>
                  <div className="sidebar-btn-label">
                    {m.label}
                    <span className="sidebar-btn-sub">{m.sub}</span>
                  </div>
                  <span className="sidebar-btn-num">{String(i + 1).padStart(2, '0')}</span>
                </button>
              );
            })}
          </nav>

          <div className="sidebar-divider"/>
          <div className="sidebar-footer">
            <div className="sidebar-footer-text">
              GeoHazard Suite v12.0<br/>
              Takahashi (1981)<br/>
              Caine (1980)<br/>
              Horn (1981)<br/>
              Box-Muller (1958)<br/>
              <br/>
              Research use only —<br/>
              verify against field data
            </div>
          </div>
        </div>

        {/* CONTENT AREA */}
        <div className="content-area">
          {/* SUB-TABS */}
          <div className="sub-tabs">
            <button
              className={`sub-tab${activeSubTab === 'analyze' ? ' active' : ''}`}
              onClick={() => setActiveSubTab('analyze')}
            >
              <FlaskConical size={12}/> Analyze
            </button>
            <button
              className={`sub-tab${activeSubTab === 'learn' ? ' active' : ''}`}
              onClick={() => setActiveSubTab('learn')}
            >
              <GraduationCap size={12}/> Learn
            </button>
          </div>

          {/* MODULE CONTENT */}
          <div className="module-content">
            {ActiveComponent && <ActiveComponent key={`${activeModule}-${activeSubTab}`}/>}
          </div>
        </div>
      </div>
    </div>
  );
}
